
/* === js/utils/constants.js === */
/**
 * Game configuration constants
 */
const GAME_CONFIG = {
    // Canvas dimensions
    CANVAS_WIDTH: 800,
    CANVAS_HEIGHT: 400,

    // Note scrolling
    SCROLL_SPEED: 150,         // Pixels per second
    NOTE_WIDTH: 80,            // Width of note blocks
    NOTE_HEIGHT: 35,           // Height of note blocks
    SPAWN_X: 850,              // Where notes appear (off-screen right)
    TARGET_X: 150,             // Where player should hit notes
    SCORING_ZONE_WIDTH: 100,   // Width of the scoring zone
    // Note: LEAD_TIME is calculated dynamically in SongPlayer based on scroll distance

    // Pitch detection
    PITCH_TOLERANCE_CENTS: 50, // Acceptable deviation (half semitone)
    DETECTION_INTERVAL: 50,    // ms between pitch checks

    // Scoring
    PERFECT_SCORE: 100,
    GOOD_SCORE: 50,

    // Note display range (MIDI notes)
    MIN_DISPLAY_NOTE: 55,      // G3
    MAX_DISPLAY_NOTE: 76,      // E5

    // Colors
    COLORS: {
        background: '#1a1a2e',
        targetLine: '#FFD700',
        scoringZone: 'rgba(255, 215, 0, 0.1)',
        pitchIndicator: '#00FF88',
        pitchIndicatorGlow: 'rgba(0, 255, 136, 0.3)',
        guideLines: '#333355',
        noteLabels: '#888',
        text: '#ffffff'
    },

    NOTE_COLORS: {
        pending: '#4A90D9',
        active: '#FFD700',
        hit: '#4CAF50',
        missed: '#E74C3C'
    },

    // Pitch guide configuration
    PITCH_GUIDE: {
        ENABLED: true,
        WIDTH: 200,           // Width of the cents meter
        HEIGHT: 30,           // Height of the meter
        MARGIN_BOTTOM: 60,    // Distance from bottom of canvas
        COLORS: {
            sharp: '#E74C3C',     // Red when too high
            flat: '#3498DB',      // Blue when too low
            onTarget: '#4CAF50',  // Green when accurate
            background: 'rgba(0, 0, 0, 0.5)',
            marker: '#ffffff'
        }
    }
};


/* === js/utils/noteUtils.js === */
/**
 * Note utility functions for frequency/MIDI conversion
 */

// Musical constants
const A4_FREQUENCY = 440;
const A4_MIDI = 69;

// Note names in chromatic order
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Solfege names (relative to key)
const SOLFEGE = ['Do', 'Di', 'Re', 'Ri', 'Mi', 'Fa', 'Fi', 'Sol', 'Si', 'La', 'Li', 'Ti'];

/**
 * Convert frequency (Hz) to MIDI note number
 * @param {number} frequency - Frequency in Hz
 * @returns {number} MIDI note number (can be fractional), or NaN if invalid
 */
function frequencyToMidi(frequency) {
    if (frequency <= 0 || !isFinite(frequency)) {
        return NaN;
    }
    return 12 * Math.log2(frequency / A4_FREQUENCY) + A4_MIDI;
}

/**
 * Convert frequency to nearest MIDI note number (rounded)
 * @param {number} frequency - Frequency in Hz
 * @returns {number} Nearest MIDI note number
 */
function frequencyToMidiRounded(frequency) {
    return Math.round(frequencyToMidi(frequency));
}

/**
 * Convert MIDI note number to frequency (Hz)
 * @param {number} midi - MIDI note number
 * @returns {number} Frequency in Hz
 */
function midiToFrequency(midi) {
    return A4_FREQUENCY * Math.pow(2, (midi - A4_MIDI) / 12);
}

/**
 * Get note name from MIDI number (e.g., "C4", "F#5")
 * @param {number} midi - MIDI note number
 * @returns {string} Note name with octave
 */
function midiToNoteName(midi) {
    if (!isFinite(midi)) {
        return '?';
    }
    const octave = Math.floor(midi / 12) - 1;
    // Handle negative MIDI numbers correctly
    const noteIndex = ((midi % 12) + 12) % 12;
    return NOTE_NAMES[noteIndex] + octave;
}

/**
 * Get solfege name from MIDI number relative to key
 * @param {number} midi - MIDI note number
 * @param {number} keyMidi - Root note MIDI number (default C4 = 60)
 * @returns {string} Solfege name
 */
function midiToSolfege(midi, keyMidi = 60) {
    const interval = ((midi - keyMidi) % 12 + 12) % 12;
    return SOLFEGE[interval];
}

/**
 * Calculate cents deviation between two frequencies
 * 100 cents = 1 semitone
 * @param {number} detectedFreq - Detected frequency
 * @param {number} targetFreq - Target frequency
 * @returns {number} Cents deviation (positive = sharp, negative = flat), or 0 if invalid
 */
function getCentsDeviation(detectedFreq, targetFreq) {
    if (detectedFreq <= 0 || targetFreq <= 0 || !isFinite(detectedFreq) || !isFinite(targetFreq)) {
        return 0;
    }
    return 1200 * Math.log2(detectedFreq / targetFreq);
}

/**
 * Check if detected pitch is within acceptable range of target
 * @param {number} detectedFreq - Detected frequency in Hz
 * @param {number} targetFreq - Target frequency in Hz
 * @param {number} toleranceCents - Acceptable deviation in cents
 * @returns {boolean} True if pitch is accurate
 */
function isPitchAccurate(detectedFreq, targetFreq, toleranceCents = 50) {
    const cents = Math.abs(getCentsDeviation(detectedFreq, targetFreq));
    return cents <= toleranceCents;
}

/**
 * Get a human-readable pitch accuracy description
 * @param {number} detectedFreq - Detected frequency
 * @param {number} targetFreq - Target frequency
 * @returns {string} Description like "Perfect", "Sharp", "Flat"
 */
function getPitchAccuracyLabel(detectedFreq, targetFreq) {
    const cents = getCentsDeviation(detectedFreq, targetFreq);
    const absCents = Math.abs(cents);

    if (absCents <= 10) return 'Perfect';
    if (absCents <= 25) return cents > 0 ? 'Slightly Sharp' : 'Slightly Flat';
    if (absCents <= 50) return cents > 0 ? 'Sharp' : 'Flat';
    return cents > 0 ? 'Too Sharp' : 'Too Flat';
}


/* === js/utils/StorageManager.js === */
/**
 * StorageManager - Handles persistence for scores, achievements, and stats
 */
class StorageManager {
    static KEYS = {
        SCORES: 'perfectnote_scores',
        ACHIEVEMENTS: 'perfectnote_achievements',
        STATS: 'perfectnote_stats',
        SETTINGS: 'perfectnote_settings'
    };

    static MAX_SONGS = 50; // Limit stored songs to prevent quota issues

    // Achievement definitions
    static ACHIEVEMENTS = {
        first_song: { name: 'First Song', description: 'Complete any song', icon: '1' },
        perfect_run: { name: 'Perfect Run', description: 'Get S-grade on any song', icon: 'S' },
        streak_master: { name: 'Streak Master', description: 'Get a 20+ streak', icon: '20' },
        warm_up: { name: 'Warm Up', description: 'Play 5 games', icon: '5' },
        dedicated: { name: 'Dedicated', description: 'Play 25 games', icon: '25' },
        pitch_perfect: { name: 'Pitch Perfect', description: 'Hit 10 notes with <20 cent deviation', icon: '*' },
        no_miss: { name: 'No Miss', description: 'Complete a song with 100% accuracy', icon: '!' },
        comeback: { name: 'Comeback Kid', description: 'Recover from F-pace to B+ grade', icon: 'C' },
        speed_demon: { name: 'Speed Demon', description: 'Complete a 120+ BPM song', icon: 'F' },
        shield_user: { name: 'Shield Bearer', description: 'Use streak shield 3 times', icon: 'O' }
    };

    /**
     * Get all scores data
     */
    static getScores() {
        try {
            const data = localStorage.getItem(this.KEYS.SCORES);
            return data ? JSON.parse(data) : {};
        } catch (e) {
            console.error('Failed to load scores:', e);
            return {};
        }
    }

    /**
     * Get personal best for a specific song
     */
    static getSongBest(songId) {
        const scores = this.getScores();
        return scores[songId] || null;
    }

    /**
     * Save personal best for a song (only if it beats existing)
     * Returns { isNewBest, previousBest, delta }
     */
    static saveSongScore(songId, score, grade, maxStreak, accuracy) {
        const scores = this.getScores();
        const existing = scores[songId];

        const isNewBest = !existing || score > existing.score;
        const previousBest = existing ? existing.score : null;
        const delta = previousBest !== null ? score - previousBest : null;

        // Always update play count and last played
        if (!scores[songId]) {
            scores[songId] = {};
        }
        scores[songId].playCount = (scores[songId].playCount || 0) + 1;
        scores[songId].lastPlayed = Date.now();

        // Only update best if this score beats it
        if (isNewBest) {
            scores[songId].score = score;
            scores[songId].grade = grade;
            scores[songId].maxStreak = maxStreak;
            scores[songId].accuracy = accuracy;
        }

        // Prune old entries if we have too many
        this._pruneOldScores(scores);

        try {
            localStorage.setItem(this.KEYS.SCORES, JSON.stringify(scores));
        } catch (e) {
            console.error('Failed to save score:', e);
        }

        return { isNewBest, previousBest, delta };
    }

    /**
     * Remove oldest entries to stay under limit
     */
    static _pruneOldScores(scores) {
        const entries = Object.entries(scores);
        if (entries.length <= this.MAX_SONGS) return;

        // Sort by lastPlayed, oldest first
        entries.sort((a, b) => (a[1].lastPlayed || 0) - (b[1].lastPlayed || 0));

        // Remove oldest entries
        const toRemove = entries.length - this.MAX_SONGS;
        for (let i = 0; i < toRemove; i++) {
            delete scores[entries[i][0]];
        }
    }

    /**
     * Get global stats
     */
    static getStats() {
        try {
            const data = localStorage.getItem(this.KEYS.STATS);
            return data ? JSON.parse(data) : {
                totalGames: 0,
                totalNotesHit: 0,
                totalNotesMissed: 0,
                longestStreak: 0,
                totalShieldsUsed: 0,
                perfectPitchCount: 0
            };
        } catch (e) {
            console.error('Failed to load stats:', e);
            return {};
        }
    }

    /**
     * Update global stats after a game
     */
    static updateStats(gameStats) {
        const stats = this.getStats();

        stats.totalGames = (stats.totalGames || 0) + 1;
        stats.totalNotesHit = (stats.totalNotesHit || 0) + (gameStats.notesHit || 0);
        stats.totalNotesMissed = (stats.totalNotesMissed || 0) + (gameStats.notesMissed || 0);
        stats.longestStreak = Math.max(stats.longestStreak || 0, gameStats.maxStreak || 0);

        if (gameStats.shieldsUsed) {
            stats.totalShieldsUsed = (stats.totalShieldsUsed || 0) + gameStats.shieldsUsed;
        }
        if (gameStats.perfectPitchHits) {
            stats.perfectPitchCount = (stats.perfectPitchCount || 0) + gameStats.perfectPitchHits;
        }

        try {
            localStorage.setItem(this.KEYS.STATS, JSON.stringify(stats));
        } catch (e) {
            console.error('Failed to save stats:', e);
        }

        return stats;
    }

    /**
     * Get all unlocked achievements
     */
    static getAchievements() {
        try {
            const data = localStorage.getItem(this.KEYS.ACHIEVEMENTS);
            return data ? JSON.parse(data) : {};
        } catch (e) {
            console.error('Failed to load achievements:', e);
            return {};
        }
    }

    /**
     * Check if an achievement is unlocked
     */
    static hasAchievement(id) {
        const achievements = this.getAchievements();
        return !!achievements[id];
    }

    /**
     * Unlock an achievement
     * Returns true if newly unlocked, false if already had it
     */
    static unlockAchievement(id) {
        if (!this.ACHIEVEMENTS[id]) {
            console.warn('Unknown achievement:', id);
            return false;
        }

        const achievements = this.getAchievements();

        if (achievements[id]) {
            return false; // Already unlocked
        }

        achievements[id] = {
            unlockedAt: Date.now()
        };

        try {
            localStorage.setItem(this.KEYS.ACHIEVEMENTS, JSON.stringify(achievements));
        } catch (e) {
            console.error('Failed to save achievement:', e);
        }

        return true;
    }

    /**
     * Check and unlock achievements based on game results
     * Returns array of newly unlocked achievement IDs
     */
    static checkAchievements(results, songTempo) {
        const newlyUnlocked = [];
        const stats = this.getStats();

        // First Song - complete any song
        if (this.unlockAchievement('first_song')) {
            newlyUnlocked.push('first_song');
        }

        // Perfect Run - S grade
        if (results.grade === 'S' && this.unlockAchievement('perfect_run')) {
            newlyUnlocked.push('perfect_run');
        }

        // Streak Master - 20+ streak
        if (results.maxStreak >= 20 && this.unlockAchievement('streak_master')) {
            newlyUnlocked.push('streak_master');
        }

        // Warm Up - 5 games
        if (stats.totalGames >= 5 && this.unlockAchievement('warm_up')) {
            newlyUnlocked.push('warm_up');
        }

        // Dedicated - 25 games
        if (stats.totalGames >= 25 && this.unlockAchievement('dedicated')) {
            newlyUnlocked.push('dedicated');
        }

        // Pitch Perfect - tracked in game, checked via stats
        if (stats.perfectPitchCount >= 10 && this.unlockAchievement('pitch_perfect')) {
            newlyUnlocked.push('pitch_perfect');
        }

        // No Miss - 100% accuracy
        if (parseFloat(results.accuracy) >= 100 && this.unlockAchievement('no_miss')) {
            newlyUnlocked.push('no_miss');
        }

        // Speed Demon - 120+ BPM song
        if (songTempo >= 120 && this.unlockAchievement('speed_demon')) {
            newlyUnlocked.push('speed_demon');
        }

        // Shield Bearer - 3 shields used total
        if (stats.totalShieldsUsed >= 3 && this.unlockAchievement('shield_user')) {
            newlyUnlocked.push('shield_user');
        }

        return newlyUnlocked;
    }

    /**
     * Get settings
     */
    static getSettings() {
        try {
            const data = localStorage.getItem(this.KEYS.SETTINGS);
            return data ? JSON.parse(data) : {
                soundEffects: true,
                soundVolume: 0.5
            };
        } catch (e) {
            return { soundEffects: true, soundVolume: 0.5 };
        }
    }

    /**
     * Save settings
     */
    static saveSettings(settings) {
        try {
            localStorage.setItem(this.KEYS.SETTINGS, JSON.stringify(settings));
        } catch (e) {
            console.error('Failed to save settings:', e);
        }
    }

    /**
     * Calculate proximity to next grade
     * Returns { nextGrade, percentNeeded } or null if already S
     */
    static getNextGradeInfo(accuracy) {
        const acc = parseFloat(accuracy);
        const thresholds = [
            { grade: 'S', min: 95 },
            { grade: 'A', min: 90 },
            { grade: 'B', min: 80 },
            { grade: 'C', min: 70 },
            { grade: 'D', min: 60 }
        ];

        for (const threshold of thresholds) {
            if (acc < threshold.min) {
                return {
                    nextGrade: threshold.grade,
                    percentNeeded: (threshold.min - acc).toFixed(1)
                };
            }
        }

        return null; // Already S grade
    }
}


/* === js/utils/SongImporter.js === */
/**
 * SongImporter - Handles custom song import, validation, and storage
 */
class SongImporter {
    static MIDI_MIN = 21;   // A0
    static MIDI_MAX = 108;  // C8
    static TEMPO_MIN = 30;
    static TEMPO_MAX = 300;
    static MAX_NOTES = 500;
    static STORAGE_KEY = 'perfectnote_custom_songs';

    /**
     * Validate a song JSON object
     * @param {Object} json - The song data to validate
     * @returns {{ valid: boolean, errors: string[] }}
     */
    static validate(json) {
        const errors = [];

        // Check if json is an object
        if (!json || typeof json !== 'object') {
            errors.push('Invalid song format: expected JSON object');
            return { valid: false, errors };
        }

        // Name validation
        if (!json.name || typeof json.name !== 'string') {
            errors.push('Song name is required');
        } else if (json.name.trim().length === 0) {
            errors.push('Song name cannot be empty');
        } else if (json.name.length > 100) {
            errors.push('Song name must be under 100 characters');
        }

        // Tempo validation
        if (json.tempo === undefined || json.tempo === null) {
            errors.push('Tempo (BPM) is required');
        } else if (typeof json.tempo !== 'number' || isNaN(json.tempo)) {
            errors.push('Tempo must be a number');
        } else if (json.tempo < this.TEMPO_MIN || json.tempo > this.TEMPO_MAX) {
            errors.push(`Tempo must be between ${this.TEMPO_MIN} and ${this.TEMPO_MAX} BPM`);
        }

        // Notes validation
        if (!Array.isArray(json.notes)) {
            errors.push('Notes array is required');
        } else if (json.notes.length === 0) {
            errors.push('At least one note is required');
        } else if (json.notes.length > this.MAX_NOTES) {
            errors.push(`Maximum ${this.MAX_NOTES} notes allowed`);
        } else {
            // Validate each note (limit error messages to first 5)
            let noteErrors = 0;
            for (let i = 0; i < json.notes.length && noteErrors < 5; i++) {
                const note = json.notes[i];

                if (!note || typeof note !== 'object') {
                    errors.push(`Note ${i + 1}: Invalid note format`);
                    noteErrors++;
                    continue;
                }

                if (typeof note.midi !== 'number' || isNaN(note.midi)) {
                    errors.push(`Note ${i + 1}: MIDI value must be a number`);
                    noteErrors++;
                } else if (note.midi < this.MIDI_MIN || note.midi > this.MIDI_MAX) {
                    errors.push(`Note ${i + 1}: MIDI value must be between ${this.MIDI_MIN} and ${this.MIDI_MAX}`);
                    noteErrors++;
                }

                if (typeof note.duration !== 'number' || isNaN(note.duration)) {
                    errors.push(`Note ${i + 1}: Duration must be a number`);
                    noteErrors++;
                } else if (note.duration < 0.125 || note.duration > 8) {
                    errors.push(`Note ${i + 1}: Duration must be between 0.125 and 8 beats`);
                    noteErrors++;
                }
            }

            if (noteErrors >= 5) {
                errors.push('...and more note errors (showing first 5)');
            }
        }

        return { valid: errors.length === 0, errors };
    }

    /**
     * Import and validate a song
     * @param {Object} json - The song data to import
     * @returns {Object} The sanitized song object
     * @throws {Error} If validation fails
     */
    static import(json) {
        const validation = this.validate(json);
        if (!validation.valid) {
            throw new Error(validation.errors.join('\n'));
        }
        return this.sanitize(json);
    }

    /**
     * Sanitize and normalize song data
     * @param {Object} json - The raw song data
     * @returns {Object} Sanitized song object
     */
    static sanitize(json) {
        return {
            id: json.id || 'custom-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            name: json.name.trim().substring(0, 100),
            tempo: Math.round(json.tempo),
            key: (json.key || 'C').substring(0, 10),
            difficulty: ['easy', 'medium', 'hard'].includes(json.difficulty) ? json.difficulty : 'medium',
            author: (json.author || 'Unknown').substring(0, 50),
            isCustom: true,
            notes: json.notes.map(n => ({
                midi: Math.round(n.midi),
                duration: Math.max(0.125, Math.min(8, n.duration)),
                lyric: (n.lyric || '').substring(0, 20)
            }))
        };
    }

    /**
     * Save custom songs to localStorage
     * @param {Array} songs - Array of custom songs
     */
    static saveToStorage(songs) {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(songs));
        } catch (e) {
            console.error('Failed to save songs to localStorage:', e);
        }
    }

    /**
     * Load custom songs from localStorage
     * @returns {Array} Array of custom songs
     */
    static loadFromStorage() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            if (!data) return [];

            const songs = JSON.parse(data);
            // Re-validate loaded songs to ensure they're still valid
            return songs.filter(song => this.validate(song).valid);
        } catch (e) {
            console.error('Failed to load songs from localStorage:', e);
            return [];
        }
    }

    /**
     * Add a song to storage
     * @param {Object} song - The song to add
     * @returns {Array} Updated list of custom songs
     */
    static addToStorage(song) {
        const songs = this.loadFromStorage();

        // Check for duplicate ID
        const existingIndex = songs.findIndex(s => s.id === song.id);
        if (existingIndex >= 0) {
            songs[existingIndex] = song; // Replace existing
        } else {
            songs.push(song);
        }

        this.saveToStorage(songs);
        return songs;
    }

    /**
     * Remove a song from storage
     * @param {string} songId - The ID of the song to remove
     * @returns {Array} Updated list of custom songs
     */
    static removeFromStorage(songId) {
        const songs = this.loadFromStorage();
        const filtered = songs.filter(s => s.id !== songId);
        this.saveToStorage(filtered);
        return filtered;
    }

    /**
     * Export a song to JSON string
     * @param {Object} song - The song to export
     * @returns {string} JSON string
     */
    static exportToJson(song) {
        const exportable = {
            name: song.name,
            tempo: song.tempo,
            key: song.key,
            difficulty: song.difficulty,
            author: song.author,
            notes: song.notes.map(n => ({
                midi: n.midi,
                duration: n.duration,
                lyric: n.lyric || undefined
            }))
        };
        return JSON.stringify(exportable, null, 2);
    }
}


/* === js/data/songs.js === */
/**
 * Song definitions
 *
 * Format:
 * - name: Display name
 * - tempo: BPM (beats per minute)
 * - key: Root MIDI note (for solfege display)
 * - notes: Array of { midi, duration, lyric }
 *   - midi: MIDI note number (60 = C4, 62 = D4, etc.)
 *   - duration: Duration in beats (1 = quarter note)
 *   - lyric: Text to display on the note
 */
const SONGS = [
    {
        id: 'ascending-scale',
        name: 'Do-Re-Mi (Ascending)',
        description: 'Practice the ascending major scale',
        tempo: 70,
        key: 60, // C4
        notes: [
            { midi: 60, duration: 1.5, lyric: 'Do' },   // C4
            { midi: 62, duration: 1.5, lyric: 'Re' },   // D4
            { midi: 64, duration: 1.5, lyric: 'Mi' },   // E4
            { midi: 65, duration: 1.5, lyric: 'Fa' },   // F4
            { midi: 67, duration: 1.5, lyric: 'Sol' },  // G4
            { midi: 69, duration: 1.5, lyric: 'La' },   // A4
            { midi: 71, duration: 1.5, lyric: 'Ti' },   // B4
            { midi: 72, duration: 2, lyric: 'Do' }      // C5
        ]
    },
    {
        id: 'descending-scale',
        name: 'Do-Ti-La (Descending)',
        description: 'Practice the descending major scale',
        tempo: 70,
        key: 60,
        notes: [
            { midi: 72, duration: 1.5, lyric: 'Do' },   // C5
            { midi: 71, duration: 1.5, lyric: 'Ti' },   // B4
            { midi: 69, duration: 1.5, lyric: 'La' },   // A4
            { midi: 67, duration: 1.5, lyric: 'Sol' },  // G4
            { midi: 65, duration: 1.5, lyric: 'Fa' },   // F4
            { midi: 64, duration: 1.5, lyric: 'Mi' },   // E4
            { midi: 62, duration: 1.5, lyric: 'Re' },   // D4
            { midi: 60, duration: 2, lyric: 'Do' }      // C4
        ]
    },
    {
        id: 'mary-lamb',
        name: 'Mary Had a Little Lamb',
        description: 'A classic nursery rhyme',
        tempo: 110,
        key: 60,
        notes: [
            // "Ma-ry had a lit-tle lamb"
            { midi: 64, duration: 1, lyric: 'Ma' },
            { midi: 62, duration: 1, lyric: 'ry' },
            { midi: 60, duration: 1, lyric: 'had' },
            { midi: 62, duration: 1, lyric: 'a' },
            { midi: 64, duration: 1, lyric: 'lit' },
            { midi: 64, duration: 1, lyric: 'tle' },
            { midi: 64, duration: 2, lyric: 'lamb' },

            // "lit-tle lamb, lit-tle lamb"
            { midi: 62, duration: 1, lyric: 'lit' },
            { midi: 62, duration: 1, lyric: 'tle' },
            { midi: 62, duration: 2, lyric: 'lamb' },
            { midi: 64, duration: 1, lyric: 'lit' },
            { midi: 67, duration: 1, lyric: 'tle' },
            { midi: 67, duration: 2, lyric: 'lamb' },

            // "Ma-ry had a lit-tle lamb, its"
            { midi: 64, duration: 1, lyric: 'Ma' },
            { midi: 62, duration: 1, lyric: 'ry' },
            { midi: 60, duration: 1, lyric: 'had' },
            { midi: 62, duration: 1, lyric: 'a' },
            { midi: 64, duration: 1, lyric: 'lit' },
            { midi: 64, duration: 1, lyric: 'tle' },
            { midi: 64, duration: 1, lyric: 'lamb' },
            { midi: 64, duration: 1, lyric: 'its' },

            // "fleece was white as snow"
            { midi: 62, duration: 1, lyric: 'fleece' },
            { midi: 62, duration: 1, lyric: 'was' },
            { midi: 64, duration: 1, lyric: 'white' },
            { midi: 62, duration: 1, lyric: 'as' },
            { midi: 60, duration: 3, lyric: 'snow' }
        ]
    },
    {
        id: 'twinkle',
        name: 'Twinkle Twinkle Little Star',
        description: 'Everyone knows this one!',
        tempo: 90,
        key: 60,
        notes: [
            // "Twin-kle twin-kle"
            { midi: 60, duration: 1, lyric: 'Twin' },   // C
            { midi: 60, duration: 1, lyric: 'kle' },    // C
            { midi: 67, duration: 1, lyric: 'twin' },   // G
            { midi: 67, duration: 1, lyric: 'kle' },    // G
            // "lit-tle star"
            { midi: 69, duration: 1, lyric: 'lit' },    // A
            { midi: 69, duration: 1, lyric: 'tle' },    // A
            { midi: 67, duration: 2, lyric: 'star' },   // G (hold)
            // "How I won-der"
            { midi: 65, duration: 1, lyric: 'How' },    // F
            { midi: 65, duration: 1, lyric: 'I' },      // F
            { midi: 64, duration: 1, lyric: 'won' },    // E
            { midi: 64, duration: 1, lyric: 'der' },    // E
            // "what you are"
            { midi: 62, duration: 1, lyric: 'what' },   // D
            { midi: 62, duration: 1, lyric: 'you' },    // D
            { midi: 60, duration: 2, lyric: 'are' }     // C (hold)
        ]
    },
    {
        id: 'happy-birthday',
        name: 'Happy Birthday',
        description: 'The classic birthday song',
        tempo: 120,  // In 3/4 time, quarter = 120
        key: 60,
        notes: [
            // "Happy birthday to you" (3/4 time, pickup notes)
            { midi: 60, duration: 0.5, lyric: 'Hap' },
            { midi: 60, duration: 0.5, lyric: 'py' },
            { midi: 62, duration: 1, lyric: 'birth' },
            { midi: 60, duration: 1, lyric: 'day' },
            { midi: 65, duration: 1, lyric: 'to' },
            { midi: 64, duration: 2, lyric: 'you' },

            // "Happy birthday to you"
            { midi: 60, duration: 0.5, lyric: 'Hap' },
            { midi: 60, duration: 0.5, lyric: 'py' },
            { midi: 62, duration: 1, lyric: 'birth' },
            { midi: 60, duration: 1, lyric: 'day' },
            { midi: 67, duration: 1, lyric: 'to' },
            { midi: 65, duration: 2, lyric: 'you' },

            // "Happy birthday dear [name]"
            { midi: 60, duration: 0.5, lyric: 'Hap' },
            { midi: 60, duration: 0.5, lyric: 'py' },
            { midi: 72, duration: 1, lyric: 'birth' },
            { midi: 69, duration: 1, lyric: 'day' },
            { midi: 65, duration: 1.5, lyric: 'dear' },
            { midi: 64, duration: 0.5, lyric: 'friend' },
            { midi: 62, duration: 2, lyric: '' },

            // "Happy birthday to you"
            { midi: 70, duration: 0.5, lyric: 'Hap' },
            { midi: 70, duration: 0.5, lyric: 'py' },
            { midi: 69, duration: 1, lyric: 'birth' },
            { midi: 65, duration: 1, lyric: 'day' },
            { midi: 67, duration: 1, lyric: 'to' },
            { midi: 65, duration: 3, lyric: 'you' }
        ]
    },
    {
        id: 'ode-to-joy',
        name: 'Ode to Joy',
        description: 'Beethoven\'s famous melody',
        tempo: 108,
        key: 60,
        notes: [
            // Line 1: E E F G | G F E D
            { midi: 64, duration: 1, lyric: 'Joy' },
            { midi: 64, duration: 1, lyric: 'ful' },
            { midi: 65, duration: 1, lyric: 'joy' },
            { midi: 67, duration: 1, lyric: 'ful' },

            { midi: 67, duration: 1, lyric: 'we' },
            { midi: 65, duration: 1, lyric: 'a' },
            { midi: 64, duration: 1, lyric: 'dore' },
            { midi: 62, duration: 1, lyric: 'thee' },

            // Line 2: C C D E | E. D D
            { midi: 60, duration: 1, lyric: 'God' },
            { midi: 60, duration: 1, lyric: 'of' },
            { midi: 62, duration: 1, lyric: 'glo' },
            { midi: 64, duration: 1, lyric: 'ry' },

            { midi: 64, duration: 1.5, lyric: 'Lord' },
            { midi: 62, duration: 0.5, lyric: 'of' },
            { midi: 62, duration: 2, lyric: 'love' },

            // Line 3: E E F G | G F E D (repeat)
            { midi: 64, duration: 1, lyric: 'Hearts' },
            { midi: 64, duration: 1, lyric: 'un' },
            { midi: 65, duration: 1, lyric: 'fold' },
            { midi: 67, duration: 1, lyric: 'like' },

            { midi: 67, duration: 1, lyric: 'flow' },
            { midi: 65, duration: 1, lyric: 'ers' },
            { midi: 64, duration: 1, lyric: 'be' },
            { midi: 62, duration: 1, lyric: 'fore' },

            // Line 4: C C D E | D. C C
            { midi: 60, duration: 1, lyric: 'thee' },
            { midi: 60, duration: 1, lyric: 'o' },
            { midi: 62, duration: 1, lyric: 'pen' },
            { midi: 64, duration: 1, lyric: 'ing' },

            { midi: 62, duration: 1.5, lyric: 'to' },
            { midi: 60, duration: 0.5, lyric: 'the' },
            { midi: 60, duration: 2, lyric: 'sun' }
        ]
    },
    {
        id: 'row-boat',
        name: 'Row Row Row Your Boat',
        description: 'A fun round to sing',
        tempo: 90,  // 6/8 time, dotted quarter = 90
        key: 60,
        notes: [
            // "Row row row your boat" (6/8 feel: dotted quarters and eighths)
            { midi: 60, duration: 1.5, lyric: 'Row' },
            { midi: 60, duration: 1.5, lyric: 'row' },
            { midi: 60, duration: 1, lyric: 'row' },
            { midi: 62, duration: 0.5, lyric: 'your' },
            { midi: 64, duration: 1.5, lyric: 'boat' },

            // "Gently down the stream"
            { midi: 64, duration: 1, lyric: 'Gent' },
            { midi: 62, duration: 0.5, lyric: 'ly' },
            { midi: 64, duration: 1, lyric: 'down' },
            { midi: 65, duration: 0.5, lyric: 'the' },
            { midi: 67, duration: 1.5, lyric: 'stream' },

            // "Merrily merrily merrily merrily" (even eighth notes)
            { midi: 72, duration: 0.5, lyric: 'Mer' },
            { midi: 72, duration: 0.5, lyric: 'ri' },
            { midi: 72, duration: 0.5, lyric: 'ly' },
            { midi: 67, duration: 0.5, lyric: 'mer' },
            { midi: 67, duration: 0.5, lyric: 'ri' },
            { midi: 67, duration: 0.5, lyric: 'ly' },
            { midi: 64, duration: 0.5, lyric: 'mer' },
            { midi: 64, duration: 0.5, lyric: 'ri' },
            { midi: 64, duration: 0.5, lyric: 'ly' },
            { midi: 60, duration: 0.5, lyric: 'mer' },
            { midi: 60, duration: 0.5, lyric: 'ri' },
            { midi: 60, duration: 0.5, lyric: 'ly' },

            // "Life is but a dream"
            { midi: 67, duration: 1, lyric: 'Life' },
            { midi: 65, duration: 0.5, lyric: 'is' },
            { midi: 64, duration: 1, lyric: 'but' },
            { midi: 62, duration: 0.5, lyric: 'a' },
            { midi: 60, duration: 3, lyric: 'dream' }
        ]
    },
    {
        id: 'hot-cross-buns',
        name: 'Hot Cross Buns',
        description: 'A simple beginner melody',
        tempo: 100,
        key: 60,
        notes: [
            { midi: 64, duration: 1, lyric: 'Hot' },
            { midi: 62, duration: 1, lyric: 'cross' },
            { midi: 60, duration: 2, lyric: 'buns' },

            { midi: 64, duration: 1, lyric: 'Hot' },
            { midi: 62, duration: 1, lyric: 'cross' },
            { midi: 60, duration: 2, lyric: 'buns' },

            { midi: 60, duration: 0.5, lyric: 'One' },
            { midi: 60, duration: 0.5, lyric: 'a' },
            { midi: 60, duration: 0.5, lyric: 'pen' },
            { midi: 60, duration: 0.5, lyric: 'ny' },
            { midi: 62, duration: 0.5, lyric: 'two' },
            { midi: 62, duration: 0.5, lyric: 'a' },
            { midi: 62, duration: 0.5, lyric: 'pen' },
            { midi: 62, duration: 0.5, lyric: 'ny' },

            { midi: 64, duration: 1, lyric: 'Hot' },
            { midi: 62, duration: 1, lyric: 'cross' },
            { midi: 60, duration: 2, lyric: 'buns' }
        ]
    },
    {
        id: 'jingle-bells',
        name: 'Jingle Bells',
        description: 'The famous Christmas chorus',
        tempo: 120,
        key: 60,
        notes: [
            // "Jingle bells, jingle bells"
            { midi: 64, duration: 1, lyric: 'Jin' },
            { midi: 64, duration: 1, lyric: 'gle' },
            { midi: 64, duration: 2, lyric: 'bells' },

            { midi: 64, duration: 1, lyric: 'Jin' },
            { midi: 64, duration: 1, lyric: 'gle' },
            { midi: 64, duration: 2, lyric: 'bells' },

            // "Jingle all the way"
            { midi: 64, duration: 1, lyric: 'Jin' },
            { midi: 67, duration: 1, lyric: 'gle' },
            { midi: 60, duration: 1.5, lyric: 'all' },
            { midi: 62, duration: 0.5, lyric: 'the' },
            { midi: 64, duration: 4, lyric: 'way' },

            // "Oh what fun it is to ride"
            { midi: 65, duration: 1, lyric: 'Oh' },
            { midi: 65, duration: 1, lyric: 'what' },
            { midi: 65, duration: 1.5, lyric: 'fun' },
            { midi: 65, duration: 0.5, lyric: 'it' },
            { midi: 65, duration: 1, lyric: 'is' },
            { midi: 64, duration: 1, lyric: 'to' },
            { midi: 64, duration: 1, lyric: 'ride' },
            { midi: 64, duration: 1, lyric: 'in' },

            // "a one-horse open sleigh"
            { midi: 67, duration: 1, lyric: 'a' },
            { midi: 67, duration: 1, lyric: 'one' },
            { midi: 65, duration: 1, lyric: 'horse' },
            { midi: 62, duration: 1, lyric: 'o' },
            { midi: 69, duration: 1, lyric: 'pen' },
            { midi: 67, duration: 3, lyric: 'sleigh' }
        ]
    }
];


/* === js/audio/AudioManager.js === */
/**
 * AudioManager - Handles Web Audio API setup and microphone access
 */
class AudioManager {
    constructor() {
        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.mediaStream = null;  // Store stream for proper cleanup
        this.bufferLength = 4096;  // Increased for better low-frequency resolution
        this.dataArray = null;
        this.isInitialized = false;
    }

    /**
     * Initialize audio context and request microphone permission
     */
    async initialize() {
        // Check for browser support
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('Browser does not support audio input. Please use a modern browser.');
        }

        try {
            // Create audio context
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Request microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            });

            // Create audio nodes
            this.microphone = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = this.bufferLength;
            this.analyser.smoothingTimeConstant = 0.1;

            // Connect microphone to analyser (but not to destination to avoid feedback)
            this.microphone.connect(this.analyser);

            // Create buffer for audio data
            this.dataArray = new Float32Array(this.bufferLength);

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('Failed to initialize audio:', error);
            throw error;
        }
    }

    /**
     * Get current audio time-domain data
     */
    getAudioData() {
        if (!this.isInitialized) return null;
        this.analyser.getFloatTimeDomainData(this.dataArray);
        return this.dataArray;
    }

    /**
     * Get the sample rate of the audio context
     */
    getSampleRate() {
        return this.audioContext ? this.audioContext.sampleRate : 44100;
    }

    /**
     * Get the audio context for external use (e.g., tone generation)
     */
    getAudioContext() {
        return this.audioContext;
    }

    /**
     * Resume audio context (required after user interaction)
     */
    async resume() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    /**
     * Clean up audio resources
     */
    dispose() {
        // Disconnect audio nodes
        if (this.microphone) {
            this.microphone.disconnect();
            this.microphone = null;
        }

        // Stop all media stream tracks (releases microphone)
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.analyser = null;
        this.dataArray = null;
        this.isInitialized = false;
    }
}


/* === js/audio/ToneGenerator.js === */
/**
 * ToneGenerator - Generates reference tones using Web Audio API
 */
class ToneGenerator {
    constructor(audioContext) {
        this.audioContext = audioContext;
        this.oscillator = null;
        this.gainNode = null;
        this.isPlaying = false;
        this.currentFrequency = 0;
        this.enabled = false;
        this.releaseTimeout = null;  // Track pending release timeout

        // Envelope settings
        this.attackTime = 0.05;  // 50ms attack
        this.releaseTime = 0.1; // 100ms release
        this.volume = 0.3;       // 30% volume
    }

    /**
     * Enable or disable the tone generator
     */
    setEnabled(enabled) {
        this.enabled = enabled;
        if (!enabled) {
            this.stop();
        }
    }

    /**
     * Set the volume level (0.0 to 1.0)
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
        if (this.gainNode && this.isPlaying) {
            this.gainNode.gain.setTargetAtTime(
                this.volume,
                this.audioContext.currentTime,
                0.05
            );
        }
    }

    /**
     * Play a tone at the specified frequency
     */
    play(frequency) {
        if (!this.enabled || !this.audioContext) return;

        // Validate frequency
        if (frequency <= 0 || frequency > 20000 || !isFinite(frequency)) return;

        // Cancel any pending release timeout
        if (this.releaseTimeout) {
            clearTimeout(this.releaseTimeout);
            this.releaseTimeout = null;
        }

        // If already playing at this frequency, do nothing
        if (this.isPlaying && Math.abs(this.currentFrequency - frequency) < 0.1) {
            return;
        }

        // If playing different note, transition smoothly
        if (this.isPlaying && this.oscillator && this.gainNode) {
            this.oscillator.frequency.setTargetAtTime(
                frequency,
                this.audioContext.currentTime,
                0.02
            );
            this.currentFrequency = frequency;
            return;
        }

        // Create oscillator and gain nodes
        this.oscillator = this.audioContext.createOscillator();
        this.gainNode = this.audioContext.createGain();

        // Configure oscillator
        this.oscillator.type = 'sine';
        this.oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);

        // Configure gain with attack envelope
        this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        this.gainNode.gain.linearRampToValueAtTime(
            this.volume,
            this.audioContext.currentTime + this.attackTime
        );

        // Connect nodes
        this.oscillator.connect(this.gainNode);
        this.gainNode.connect(this.audioContext.destination);

        // Start the oscillator
        this.oscillator.start();
        this.isPlaying = true;
        this.currentFrequency = frequency;
    }

    /**
     * Play a tone for a specific MIDI note
     */
    playMidi(midi) {
        const frequency = midiToFrequency(midi);
        this.play(frequency);
    }

    /**
     * Stop the current tone with release envelope
     */
    stop() {
        if (!this.isPlaying || !this.gainNode || !this.oscillator) return;

        // Cancel any pending release timeout
        if (this.releaseTimeout) {
            clearTimeout(this.releaseTimeout);
            this.releaseTimeout = null;
        }

        const now = this.audioContext.currentTime;

        // Apply release envelope
        this.gainNode.gain.cancelScheduledValues(now);
        this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
        this.gainNode.gain.linearRampToValueAtTime(0, now + this.releaseTime);

        // Store references for cleanup in timeout
        const oscillator = this.oscillator;
        const gainNode = this.gainNode;

        // Stop oscillator after release
        this.releaseTimeout = setTimeout(() => {
            try {
                oscillator.stop();
                oscillator.disconnect();
                gainNode.disconnect();
            } catch (e) {
                // Oscillator may already be stopped
            }
            this.releaseTimeout = null;
        }, this.releaseTime * 1000 + 10);

        // Mark as not playing immediately (new play() can create fresh nodes)
        this.oscillator = null;
        this.gainNode = null;
        this.isPlaying = false;
        this.currentFrequency = 0;
    }

    /**
     * Clean up resources
     */
    dispose() {
        // Cancel any pending release timeout
        if (this.releaseTimeout) {
            clearTimeout(this.releaseTimeout);
            this.releaseTimeout = null;
        }
        this.stop();
        this.enabled = false;
        this.audioContext = null;
    }
}


/* === js/audio/SoundManager.js === */
/**
 * SoundManager - Procedural sound effects using Web Audio API
 * All sounds are generated programmatically, no external files needed
 */
class SoundManager {
    constructor(audioContext) {
        this.ctx = audioContext;
        this.enabled = true;
        this.volume = 0.5;
        this.masterGain = this.ctx.createGain();
        this.masterGain.connect(this.ctx.destination);
        this.masterGain.gain.value = this.volume;
    }

    /**
     * Enable or disable sound effects
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }

    /**
     * Set master volume (0-1)
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
        this.masterGain.gain.value = this.volume;
    }

    /**
     * Play a note hit sound - short rising chime
     */
    playHit() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.12;

        // Two oscillators for richer sound
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc1.type = 'sine';
        osc2.type = 'triangle';

        // Rising frequency sweep
        osc1.frequency.setValueAtTime(800, now);
        osc1.frequency.exponentialRampToValueAtTime(1200, now + duration);
        osc2.frequency.setValueAtTime(1200, now);
        osc2.frequency.exponentialRampToValueAtTime(1800, now + duration);

        // Quick attack, quick decay
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.3, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(this.masterGain);

        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + duration);
        osc2.stop(now + duration);
    }

    /**
     * Play a miss sound - low thud
     */
    playMiss() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.15;

        // Low sine wave
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(80, now + duration);

        gain.gain.setValueAtTime(0.4, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + duration);

        // Add noise burst
        this._playNoiseBurst(0.1, 0.08);
    }

    /**
     * Play a near-miss sound - softer, questioning tone
     */
    playNearMiss() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.1;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(350, now + duration);

        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + duration);
    }

    /**
     * Play streak milestone sound - triumphant chord
     */
    playStreakMilestone() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.25;

        // Major chord: root, major third, fifth
        const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5

        frequencies.forEach((freq, i) => {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();

            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now);

            // Staggered attack for arpeggio effect
            const startTime = now + i * 0.02;
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
            gain.gain.setValueAtTime(0.2, startTime + duration - 0.1);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

            osc.connect(gain);
            gain.connect(this.masterGain);

            osc.start(startTime);
            osc.stop(startTime + duration);
        });
    }

    /**
     * Play bonus note hit - sparkle sound
     */
    playBonus() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;

        // High sparkle tones
        const frequencies = [1500, 2000, 2500, 3000];

        frequencies.forEach((freq, i) => {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();

            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now + i * 0.03);

            const startTime = now + i * 0.03;
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.15, startTime + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.1);

            osc.connect(gain);
            gain.connect(this.masterGain);

            osc.start(startTime);
            osc.stop(startTime + 0.15);
        });

        // Add noise sparkle
        this._playNoiseBurst(0.1, 0.15, 3000);
    }

    /**
     * Play shield activation - power-up sound
     */
    playShieldActivate() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.3;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + duration);

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
        gain.gain.setValueAtTime(0.15, now + duration - 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        // Add filter for smoother sound
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(400, now);
        filter.frequency.exponentialRampToValueAtTime(2000, now + duration);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + duration);
    }

    /**
     * Play shield consumed - woosh sound
     */
    playShieldUsed() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;
        const duration = 0.25;

        // Filtered noise sweep
        const bufferSize = this.ctx.sampleRate * duration;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }

        const noise = this.ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(2000, now);
        filter.frequency.exponentialRampToValueAtTime(500, now + duration);
        filter.Q.value = 2;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        noise.start(now);
        noise.stop(now + duration);
    }

    /**
     * Play S-grade fanfare
     */
    playFanfare() {
        if (!this.enabled) return;

        const now = this.ctx.currentTime;

        // Ascending arpeggio
        const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6

        notes.forEach((freq, i) => {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();

            osc.type = 'sine';
            osc.frequency.value = freq;

            const startTime = now + i * 0.08;
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
            gain.gain.setValueAtTime(0.25, startTime + 0.15);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);

            osc.connect(gain);
            gain.connect(this.masterGain);

            osc.start(startTime);
            osc.stop(startTime + 0.5);
        });

        // Add sparkle at the end
        setTimeout(() => this.playBonus(), 350);
    }

    /**
     * Helper: Play a noise burst
     */
    _playNoiseBurst(volume = 0.1, duration = 0.1, highpass = 1000) {
        const now = this.ctx.currentTime;
        const bufferSize = this.ctx.sampleRate * duration;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }

        const noise = this.ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = this.ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = highpass;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(volume, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        noise.start(now);
        noise.stop(now + duration);
    }
}


/* === js/audio/PitchDetector.js === */
/**
 * PitchDetector - Real-time pitch detection using autocorrelation
 * with temporal smoothing and median filtering for improved accuracy
 */
class PitchDetector {
    constructor(audioManager) {
        this.audioManager = audioManager;
        this.minFrequency = 80;    // Hz - lowest detectable pitch
        this.maxFrequency = 1000;  // Hz - highest detectable pitch
        this.clarityThreshold = 0.85; // Minimum correlation for valid pitch (lowered for better detection)
        this.rmsThreshold = 0.008;   // Minimum volume to detect (slightly more sensitive)

        // Temporal smoothing - keep history of recent pitch readings
        this.pitchHistory = [];
        this.historySize = 5;        // Number of frames to average
        this.maxJumpCents = 100;     // Max allowed jump between frames (1 semitone)

        // Median filter for outlier rejection
        this.medianHistory = [];
        this.medianSize = 3;
    }

    /**
     * Reset the pitch detection history (call when starting new song)
     */
    reset() {
        this.pitchHistory = [];
        this.medianHistory = [];
    }

    /**
     * Detect the current pitch from microphone input
     * @returns {Object|null} { frequency, clarity } or null if no pitch detected
     */
    detectPitch() {
        const buffer = this.audioManager.getAudioData();
        if (!buffer) return null;

        const sampleRate = this.audioManager.getSampleRate();

        // Check if signal is loud enough (noise gate)
        const rms = this.calculateRMS(buffer);
        if (rms < this.rmsThreshold) {
            // Clear history when silent
            this.pitchHistory = [];
            this.medianHistory = [];
            return null;
        }

        // Perform autocorrelation to get raw pitch
        const rawResult = this.autocorrelate(buffer, sampleRate);
        if (!rawResult) {
            return null;
        }

        // Apply median filter to reject outliers
        const filteredFreq = this.applyMedianFilter(rawResult.frequency);
        if (filteredFreq === null) {
            return null;
        }

        // Apply temporal smoothing
        const smoothedFreq = this.applyTemporalSmoothing(filteredFreq);

        return {
            frequency: smoothedFreq,
            clarity: rawResult.clarity,
            rawFrequency: rawResult.frequency  // Keep raw for debugging
        };
    }

    /**
     * Apply median filter to reject outlier readings
     */
    applyMedianFilter(frequency) {
        this.medianHistory.push(frequency);
        if (this.medianHistory.length > this.medianSize) {
            this.medianHistory.shift();
        }

        if (this.medianHistory.length < this.medianSize) {
            return frequency; // Not enough samples yet
        }

        // Sort and return median
        const sorted = [...this.medianHistory].sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length / 2)];
    }

    /**
     * Apply temporal smoothing with jump detection
     */
    applyTemporalSmoothing(frequency) {
        // Check for large jumps (likely octave errors or new note)
        if (this.pitchHistory.length > 0) {
            const lastFreq = this.pitchHistory[this.pitchHistory.length - 1];
            const cents = Math.abs(1200 * Math.log2(frequency / lastFreq));

            // If jump is too large, reset history (new note started)
            if (cents > this.maxJumpCents * 2) {
                this.pitchHistory = [];
            }
        }

        this.pitchHistory.push(frequency);
        if (this.pitchHistory.length > this.historySize) {
            this.pitchHistory.shift();
        }

        // Guard against empty history (shouldn't happen, but be safe)
        if (this.pitchHistory.length === 0) {
            return frequency;
        }

        // Weighted average - more weight to recent samples
        let weightedSum = 0;
        let weightTotal = 0;
        for (let i = 0; i < this.pitchHistory.length; i++) {
            const weight = i + 1; // Linear weighting: 1, 2, 3, 4, 5
            weightedSum += this.pitchHistory[i] * weight;
            weightTotal += weight;
        }

        return weightedSum / weightTotal;
    }

    /**
     * Calculate Root Mean Square of the buffer
     */
    calculateRMS(buffer) {
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
            sum += buffer[i] * buffer[i];
        }
        return Math.sqrt(sum / buffer.length);
    }

    /**
     * Autocorrelation pitch detection algorithm
     * Based on the McLeod Pitch Method (MPM) with NSDF
     */
    autocorrelate(buffer, sampleRate) {
        const SIZE = buffer.length;
        const MAX_SAMPLES = Math.floor(sampleRate / this.minFrequency);
        const MIN_SAMPLES = Math.floor(sampleRate / this.maxFrequency);

        // Normalized Square Difference Function
        const nsdf = new Float32Array(MAX_SAMPLES);

        for (let tau = MIN_SAMPLES; tau < MAX_SAMPLES && tau < SIZE / 2; tau++) {
            let acf = 0;  // Autocorrelation
            let divisor = 0;

            for (let i = 0; i < SIZE - tau; i++) {
                acf += buffer[i] * buffer[i + tau];
                divisor += buffer[i] * buffer[i] + buffer[i + tau] * buffer[i + tau];
            }

            // Guard against division by zero (silence/very quiet input)
            if (divisor < 0.0001) {
                nsdf[tau] = 0;
            } else {
                nsdf[tau] = 2 * acf / divisor;
            }
        }

        // Find all key maxima (peaks followed by negative crossing)
        // This is the McLeod Pitch Method (MPM) approach
        const keyMaxima = [];
        let inPeak = false;
        let peakTau = 0;
        let peakVal = 0;

        for (let tau = MIN_SAMPLES; tau < MAX_SAMPLES && tau < SIZE / 2; tau++) {
            if (nsdf[tau] > 0 && !inPeak) {
                inPeak = true;
                peakTau = tau;
                peakVal = nsdf[tau];
            }

            if (inPeak) {
                if (nsdf[tau] > peakVal) {
                    peakVal = nsdf[tau];
                    peakTau = tau;
                }

                if (nsdf[tau] < 0) {
                    // End of peak - record if above threshold
                    if (peakVal >= this.clarityThreshold) {
                        keyMaxima.push({ tau: peakTau, correlation: peakVal });
                    }
                    inPeak = false;
                }
            }
        }

        if (keyMaxima.length === 0) {
            return null;
        }

        // Find the first peak that is at least 80% of the highest peak
        // This helps avoid octave errors by preferring the fundamental
        const highestCorrelation = Math.max(...keyMaxima.map(k => k.correlation));
        const threshold = highestCorrelation * 0.8;

        let bestTau = -1;
        let bestCorrelation = 0;

        for (const peak of keyMaxima) {
            if (peak.correlation >= threshold) {
                bestTau = peak.tau;
                bestCorrelation = peak.correlation;
                break; // Take the first (lowest frequency) peak above threshold
            }
        }

        if (bestTau === -1) {
            return null;
        }

        // Parabolic interpolation for sub-sample accuracy
        const refinedTau = this.parabolicInterpolation(nsdf, bestTau);
        const frequency = sampleRate / refinedTau;

        // Validate frequency is in expected range
        if (frequency < this.minFrequency || frequency > this.maxFrequency) {
            return null;
        }

        return {
            frequency: frequency,
            clarity: bestCorrelation
        };
    }

    /**
     * Parabolic interpolation around a peak for sub-sample accuracy
     */
    parabolicInterpolation(array, peakIndex) {
        if (peakIndex <= 0 || peakIndex >= array.length - 1) {
            return peakIndex;
        }

        const alpha = array[peakIndex - 1];
        const beta = array[peakIndex];
        const gamma = array[peakIndex + 1];

        const denominator = alpha - 2 * beta + gamma;
        if (denominator === 0) {
            return peakIndex;
        }

        const adjustment = 0.5 * (alpha - gamma) / denominator;
        return peakIndex + adjustment;
    }
}


/* === js/game/ScoreManager.js === */
/**
 * ScoreManager - Handles scoring and statistics
 */
class ScoreManager {
    constructor() {
        this.reset();
    }

    /**
     * Reset all scores and statistics
     */
    reset() {
        this.score = 0;
        this.notesHit = 0;
        this.notesMissed = 0;
        this.streak = 0;
        this.maxStreak = 0;
        this.totalNotes = 0;

        // Streak shield system
        this.hasShield = false;
        this.shieldsUsed = 0;
        this.lastShieldAwardedAt = 0; // Prevent awarding shield multiple times at same milestone
    }

    /**
     * Set the total number of notes in the song
     */
    setTotalNotes(count) {
        this.totalNotes = count;
    }

    /**
     * Record a successful hit
     * @returns {Object} { earnedPoints, shieldAwarded }
     */
    addHit(points = GAME_CONFIG.PERFECT_SCORE) {
        // Apply streak multiplier
        const multiplier = Math.min(1 + this.streak * 0.1, 2); // Max 2x
        const earnedPoints = Math.round(points * multiplier);

        this.score += earnedPoints;
        this.notesHit++;
        this.streak++;
        this.maxStreak = Math.max(this.maxStreak, this.streak);

        // Award shield at milestones (10, 20, 30...) if not already holding one
        let shieldAwarded = false;
        if (!this.hasShield && this.streak >= 10 && this.streak % 10 === 0 && this.streak !== this.lastShieldAwardedAt) {
            this.hasShield = true;
            this.lastShieldAwardedAt = this.streak;
            shieldAwarded = true;
        }

        return earnedPoints;
    }

    /**
     * Check if shield was just awarded (call after addHit)
     */
    wasShieldAwarded() {
        return this.hasShield && this.streak === this.lastShieldAwardedAt;
    }

    /**
     * Record a miss
     * @returns {boolean} True if shield was consumed (streak preserved)
     */
    addMiss() {
        // If we have a shield, consume it instead of breaking streak
        if (this.hasShield) {
            this.hasShield = false;
            this.shieldsUsed++;
            return true; // Shield consumed
        }

        this.notesMissed++;
        this.streak = 0;
        return false; // No shield, streak broken
    }

    /**
     * Check if player has a shield
     */
    hasStreakShield() {
        return this.hasShield;
    }

    /**
     * Get current score
     */
    getScore() {
        return this.score;
    }

    /**
     * Get current streak
     */
    getStreak() {
        return this.streak;
    }

    /**
     * Calculate accuracy percentage
     */
    getAccuracy() {
        const total = this.notesHit + this.notesMissed;
        return total > 0 ? (this.notesHit / total * 100) : 0;
    }

    /**
     * Get letter grade based on accuracy
     * Note: Rounds to 1 decimal place for consistency with displayed accuracy
     */
    getGrade() {
        const accuracy = Math.round(this.getAccuracy() * 10) / 10;
        if (accuracy >= 95) return 'S';
        if (accuracy >= 90) return 'A';
        if (accuracy >= 80) return 'B';
        if (accuracy >= 70) return 'C';
        if (accuracy >= 60) return 'D';
        return 'F';
    }

    /**
     * Get complete results summary
     */
    getResults() {
        return {
            score: this.score,
            notesHit: this.notesHit,
            notesMissed: this.notesMissed,
            totalNotes: this.totalNotes,
            accuracy: this.getAccuracy().toFixed(1),
            maxStreak: this.maxStreak,
            grade: this.getGrade(),
            shieldsUsed: this.shieldsUsed
        };
    }
}


/* === js/game/NoteRenderer.js === */
/**
 * NoteRenderer - Canvas rendering for game visuals
 */
class NoteRenderer {
    constructor(ctx) {
        this.ctx = ctx;
        this.width = GAME_CONFIG.CANVAS_WIDTH;
        this.height = GAME_CONFIG.CANVAS_HEIGHT;
    }

    /**
     * Convert MIDI note to Y position on canvas
     * Higher notes appear higher on screen
     */
    midiToY(midi) {
        const range = GAME_CONFIG.MAX_DISPLAY_NOTE - GAME_CONFIG.MIN_DISPLAY_NOTE;
        // Prevent division by zero
        if (range === 0) {
            return this.height / 2;
        }
        const normalized = (midi - GAME_CONFIG.MIN_DISPLAY_NOTE) / range;
        // Invert and add padding
        const usableHeight = this.height * 0.75;
        const topPadding = this.height * 0.1;
        return this.height - topPadding - (normalized * usableHeight);
    }

    /**
     * Draw the game background
     */
    drawBackground() {
        // Dark background
        this.ctx.fillStyle = GAME_CONFIG.COLORS.background;
        this.ctx.fillRect(0, 0, this.width, this.height);

        // Draw horizontal pitch guide lines for each note
        this.ctx.strokeStyle = GAME_CONFIG.COLORS.guideLines;
        this.ctx.lineWidth = 1;

        // Draw lines for natural notes in the display range
        const naturalNotes = [0, 2, 4, 5, 7, 9, 11]; // C, D, E, F, G, A, B
        for (let midi = GAME_CONFIG.MIN_DISPLAY_NOTE; midi <= GAME_CONFIG.MAX_DISPLAY_NOTE; midi++) {
            const noteInOctave = midi % 12;
            if (naturalNotes.includes(noteInOctave)) {
                const y = this.midiToY(midi);
                this.ctx.beginPath();
                this.ctx.moveTo(0, y);
                this.ctx.lineTo(this.width, y);
                this.ctx.stroke();

                // Add note label on the left
                this.ctx.fillStyle = GAME_CONFIG.COLORS.noteLabels;
                this.ctx.font = '10px Arial';
                this.ctx.textAlign = 'left';
                this.ctx.fillText(midiToNoteName(midi), 5, y + 3);
            }
        }
    }

    /**
     * Draw the target line where notes should be hit
     */
    drawTargetLine() {
        const x = GAME_CONFIG.TARGET_X;

        // Draw scoring zone background
        this.ctx.fillStyle = GAME_CONFIG.COLORS.scoringZone;
        this.ctx.fillRect(
            x - GAME_CONFIG.SCORING_ZONE_WIDTH / 2,
            0,
            GAME_CONFIG.SCORING_ZONE_WIDTH,
            this.height
        );

        // Draw the target line
        this.ctx.strokeStyle = GAME_CONFIG.COLORS.targetLine;
        this.ctx.lineWidth = 3;
        this.ctx.setLineDash([10, 5]);
        this.ctx.beginPath();
        this.ctx.moveTo(x, 0);
        this.ctx.lineTo(x, this.height);
        this.ctx.stroke();
        this.ctx.setLineDash([]);

        // Draw "SING" label
        this.ctx.fillStyle = GAME_CONFIG.COLORS.targetLine;
        this.ctx.font = 'bold 14px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('SING', x, this.height - 10);
    }

    /**
     * Draw a note block
     */
    drawNote(note) {
        // Validate note object has required properties
        if (!note || !isFinite(note.midi) || !isFinite(note.duration)) return;

        const y = this.midiToY(note.midi);
        const width = GAME_CONFIG.NOTE_WIDTH * note.duration;
        const height = GAME_CONFIG.NOTE_HEIGHT;

        // Validate dimensions
        if (width <= 0 || height <= 0) return;

        // Clamp corner radius to prevent overflow on short notes
        const cornerRadius = Math.min(8, width / 2, height / 2);

        // Choose color based on status
        let color = GAME_CONFIG.NOTE_COLORS[note.status || 'pending'];

        // Bonus notes are golden
        const isBonus = note.isBonus && note.status !== 'hit' && note.status !== 'missed';
        if (isBonus) {
            color = '#FFD700';
        }

        this.ctx.save();

        // Glow effect for active notes or bonus notes
        if (note.status === 'active' || isBonus) {
            this.ctx.shadowColor = isBonus ? '#FFD700' : color;
            this.ctx.shadowBlur = isBonus ? 20 : 15;
        }

        // Draw note block with rounded corners
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.roundRect(note.x, y - height / 2, width, height, cornerRadius);
        this.ctx.fill();

        // Reset shadow for border
        this.ctx.shadowBlur = 0;

        // Draw note border (thicker golden border for bonus notes)
        if (isBonus) {
            this.ctx.strokeStyle = '#fff';
            this.ctx.lineWidth = 3;
        } else {
            this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
            this.ctx.lineWidth = 2;
        }
        this.ctx.beginPath();
        this.roundRect(note.x, y - height / 2, width, height, cornerRadius);
        this.ctx.stroke();

        // Draw star indicator for bonus notes
        if (isBonus) {
            this.ctx.fillStyle = '#fff';
            this.ctx.font = 'bold 12px Arial';
            this.ctx.textAlign = 'left';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText('*2x', note.x + 4, y);
        }

        // Draw note label
        this.ctx.fillStyle = '#fff';
        this.ctx.font = 'bold 16px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        const labelX = isBonus ? note.x + width / 2 + 10 : note.x + width / 2;
        this.ctx.fillText(note.lyric || '', labelX, y);

        this.ctx.restore();
    }

    /**
     * Draw the player's current pitch indicator
     */
    drawPitchIndicator(frequency) {
        if (!frequency) return;

        const midi = frequencyToMidi(frequency);

        // Clamp to display range for visual
        if (midi < GAME_CONFIG.MIN_DISPLAY_NOTE - 2 || midi > GAME_CONFIG.MAX_DISPLAY_NOTE + 2) {
            return;
        }

        const y = this.midiToY(midi);
        const x = GAME_CONFIG.TARGET_X;

        // Draw glow
        this.ctx.fillStyle = GAME_CONFIG.COLORS.pitchIndicatorGlow;
        this.ctx.beginPath();
        this.ctx.arc(x, y, 25, 0, Math.PI * 2);
        this.ctx.fill();

        // Draw main indicator
        this.ctx.fillStyle = GAME_CONFIG.COLORS.pitchIndicator;
        this.ctx.beginPath();
        this.ctx.arc(x, y, 12, 0, Math.PI * 2);
        this.ctx.fill();

        // Draw inner circle
        this.ctx.fillStyle = '#fff';
        this.ctx.beginPath();
        this.ctx.arc(x, y, 4, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /**
     * Draw hit/miss feedback
     */
    drawFeedback(feedback) {
        if (!feedback || feedback.opacity <= 0) return;

        this.ctx.save();
        this.ctx.globalAlpha = feedback.opacity;
        this.ctx.fillStyle = feedback.hit ? '#4CAF50' : '#E74C3C';
        this.ctx.font = 'bold 24px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(
            feedback.hit ? 'HIT!' : 'MISS',
            GAME_CONFIG.TARGET_X,
            this.height / 2 + feedback.y
        );
        this.ctx.restore();
    }

    /**
     * Draw the current score with enhanced streak display
     */
    drawScore(score, streak, hasShield = false) {
        this.ctx.fillStyle = GAME_CONFIG.COLORS.text;
        this.ctx.font = 'bold 26px Arial';
        this.ctx.textAlign = 'right';
        this.ctx.fillText(`Score: ${score.toLocaleString()}`, this.width - 20, 45);

        // Draw shield indicator if active
        if (hasShield) {
            this.ctx.save();
            this.ctx.fillStyle = '#4A90D9';
            this.ctx.font = 'bold 14px Arial';
            this.ctx.textAlign = 'right';
            this.ctx.shadowColor = '#4A90D9';
            this.ctx.shadowBlur = 8;
            this.ctx.fillText('SHIELD READY', this.width - 20, 65);
            this.ctx.restore();
        }

        if (streak > 0) {
            // Determine streak tier and styling
            let streakColor = '#FFD700';
            let streakLabel = '';

            if (streak >= 20) {
                streakColor = '#FF6B6B';
                streakLabel = 'AMAZING!';
            } else if (streak >= 15) {
                streakColor = '#FF9F43';
                streakLabel = 'GREAT!';
            } else if (streak >= 10) {
                streakColor = '#4CAF50';
                streakLabel = 'GOOD!';
            } else if (streak >= 5) {
                streakColor = '#4A90D9';
                streakLabel = 'NICE!';
            }

            // Scale text based on streak
            const streakScale = Math.min(1 + streak * 0.02, 1.4);
            const streakSize = Math.round(18 * streakScale);

            this.ctx.save();

            // Glow effect for high streaks
            if (streak >= 5) {
                this.ctx.shadowColor = streakColor;
                this.ctx.shadowBlur = 8 + streak * 0.5;
            }

            this.ctx.fillStyle = streakColor;
            this.ctx.font = `bold ${streakSize}px Arial`;
            this.ctx.fillText(`${streak}x Streak`, this.width - 20, 72);

            // Streak tier label
            if (streakLabel) {
                this.ctx.font = 'bold 13px Arial';
                this.ctx.fillText(streakLabel, this.width - 20, 90);
            }

            this.ctx.restore();

            // Multiplier indicator
            const multiplier = Math.min(1 + streak * 0.1, 2);
            this.ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            this.ctx.font = '11px Arial';
            this.ctx.fillText(`${multiplier.toFixed(1)}x multiplier`, this.width - 20, streakLabel ? 106 : 90);
        }
    }

    /**
     * Draw song progress bar at top of canvas
     */
    drawProgressBar(currentTime, totalDuration) {
        if (!totalDuration || totalDuration <= 0) return;

        const barHeight = 6;
        const barY = 10;
        const barWidth = this.width - 40;
        const barX = 20;

        // Background track
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        this.ctx.beginPath();
        this.roundRect(barX, barY, barWidth, barHeight, 3);
        this.ctx.fill();

        // Progress fill
        const progress = Math.min(Math.max(currentTime / totalDuration, 0), 1);
        if (progress > 0) {
            const gradient = this.ctx.createLinearGradient(barX, 0, barX + barWidth, 0);
            gradient.addColorStop(0, '#4A90D9');
            gradient.addColorStop(1, '#FFD700');

            this.ctx.fillStyle = gradient;
            this.ctx.beginPath();
            this.roundRect(barX, barY, barWidth * progress, barHeight, 3);
            this.ctx.fill();
        }

        // Time display
        const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        this.ctx.font = '11px Arial';
        this.ctx.textAlign = 'right';
        this.ctx.fillText(
            `${formatTime(currentTime)} / ${formatTime(totalDuration)}`,
            barX + barWidth,
            barY + barHeight + 14
        );
    }

    /**
     * Draw screen flash effect
     */
    drawScreenFlash(type, intensity) {
        if (!type || intensity <= 0) return;

        this.ctx.save();
        this.ctx.globalAlpha = intensity * 0.12;

        if (type === 'hit') {
            this.ctx.fillStyle = '#4CAF50';
        } else if (type === 'miss') {
            this.ctx.fillStyle = '#E74C3C';
        } else if (type === 'streak') {
            this.ctx.fillStyle = '#FFD700';
        } else if (type === 'nearMiss') {
            this.ctx.fillStyle = '#FFA500';
        }

        this.ctx.fillRect(0, 0, this.width, this.height);
        this.ctx.restore();
    }

    /**
     * Draw a countdown before song starts
     */
    drawCountdown(count) {
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        this.ctx.fillRect(0, 0, this.width, this.height);

        this.ctx.fillStyle = '#FFD700';
        this.ctx.font = 'bold 72px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(count.toString(), this.width / 2, this.height / 2);

        this.ctx.font = '24px Arial';
        this.ctx.fillStyle = '#fff';
        this.ctx.fillText('Get Ready!', this.width / 2, this.height / 2 + 60);
    }

    /**
     * Draw practice mode blocked note indicator
     * Shows countdown timer and guidance for matching pitch
     */
    drawBlockedIndicator(note, timeRemaining, maxTime, pitchFrequency) {
        if (!note) return;

        const y = this.midiToY(note.midi);
        const x = GAME_CONFIG.TARGET_X;
        const noteWidth = GAME_CONFIG.NOTE_WIDTH * note.duration;

        // Calculate progress (1 = full time, 0 = expired)
        const progress = Math.max(0, timeRemaining / maxTime);

        // Pulsing effect for urgency
        const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 200);
        const urgencyPulse = timeRemaining < 3 ? pulse : 1;

        // Draw circular countdown timer around note
        const radius = Math.max(noteWidth / 2, GAME_CONFIG.NOTE_HEIGHT) + 15;

        // Background circle
        this.ctx.save();
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
        this.ctx.lineWidth = 6;
        this.ctx.beginPath();
        this.ctx.arc(x + noteWidth / 2, y, radius, 0, Math.PI * 2);
        this.ctx.stroke();

        // Progress arc (countdown)
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + (progress * Math.PI * 2);

        // Color changes based on time remaining
        let timerColor;
        if (timeRemaining > 5) {
            timerColor = '#4A90D9'; // Blue - plenty of time
        } else if (timeRemaining > 3) {
            timerColor = '#FFA500'; // Orange - getting low
        } else {
            timerColor = `rgba(231, 76, 60, ${urgencyPulse})`; // Red pulsing - urgent
        }

        this.ctx.strokeStyle = timerColor;
        this.ctx.lineWidth = 6;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.arc(x + noteWidth / 2, y, radius, startAngle, endAngle);
        this.ctx.stroke();

        // Draw "MATCH THIS PITCH" text with pulsing
        const textPulse = 0.7 + 0.3 * Math.sin(Date.now() / 300);
        this.ctx.globalAlpha = textPulse;
        this.ctx.fillStyle = '#FFD700';
        this.ctx.font = 'bold 16px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText('MATCH THIS PITCH', x + noteWidth / 2, y - radius - 20);

        // Draw seconds remaining
        this.ctx.globalAlpha = 1;
        this.ctx.fillStyle = timerColor;
        this.ctx.font = 'bold 14px Arial';
        this.ctx.fillText(`${Math.ceil(timeRemaining)}s`, x + noteWidth / 2, y + radius + 18);

        // Draw directional arrow from pitch indicator to note (if pitch is detected)
        if (pitchFrequency) {
            const pitchMidi = frequencyToMidi(pitchFrequency);
            const pitchY = this.midiToY(pitchMidi);
            const diff = y - pitchY; // Positive = need to go down, Negative = need to go up

            // Only show arrow if pitch is significantly off
            if (Math.abs(diff) > 20) {
                const arrowX = x - 40;
                const arrowSize = 12;

                this.ctx.fillStyle = '#FFD700';
                this.ctx.beginPath();

                if (diff > 0) {
                    // Arrow pointing down (sing lower)
                    this.ctx.moveTo(arrowX, pitchY + 30);
                    this.ctx.lineTo(arrowX - arrowSize, pitchY + 30 - arrowSize);
                    this.ctx.lineTo(arrowX + arrowSize, pitchY + 30 - arrowSize);
                } else {
                    // Arrow pointing up (sing higher)
                    this.ctx.moveTo(arrowX, pitchY - 30);
                    this.ctx.lineTo(arrowX - arrowSize, pitchY - 30 + arrowSize);
                    this.ctx.lineTo(arrowX + arrowSize, pitchY - 30 + arrowSize);
                }
                this.ctx.fill();

                // Draw hint text
                this.ctx.font = '12px Arial';
                this.ctx.textAlign = 'center';
                const hintText = diff > 0 ? 'Sing lower' : 'Sing higher';
                this.ctx.fillText(hintText, arrowX, diff > 0 ? pitchY + 50 : pitchY - 45);
            }
        }

        this.ctx.restore();
    }

    /**
     * Draw the pitch guide showing sharp/flat/on-target
     * @param {number} detectedFreq - Current detected frequency
     * @param {number} targetMidi - Target MIDI note number
     */
    drawPitchGuide(detectedFreq, targetMidi) {
        if (!GAME_CONFIG.PITCH_GUIDE.ENABLED || !detectedFreq || !targetMidi) return;

        const config = GAME_CONFIG.PITCH_GUIDE;
        const targetFreq = midiToFrequency(targetMidi);
        const cents = getCentsDeviation(detectedFreq, targetFreq);
        const tolerance = GAME_CONFIG.PITCH_TOLERANCE_CENTS;

        // Position at bottom center of canvas
        const centerX = this.width / 2;
        const y = this.height - config.MARGIN_BOTTOM;

        // Draw background
        this.ctx.fillStyle = config.COLORS.background;
        this.ctx.beginPath();
        this.roundRect(
            centerX - config.WIDTH / 2 - 10,
            y - config.HEIGHT / 2 - 5,
            config.WIDTH + 20,
            config.HEIGHT + 30,
            10
        );
        this.ctx.fill();

        // Draw meter track
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        this.ctx.fillRect(
            centerX - config.WIDTH / 2,
            y - 4,
            config.WIDTH,
            8
        );

        // Draw center target zone (green area)
        const zoneWidth = (tolerance / 100) * config.WIDTH;
        this.ctx.fillStyle = 'rgba(76, 175, 80, 0.4)';
        this.ctx.fillRect(
            centerX - zoneWidth / 2,
            y - 4,
            zoneWidth,
            8
        );

        // Draw center tick
        this.ctx.fillStyle = config.COLORS.onTarget;
        this.ctx.fillRect(centerX - 1, y - 8, 2, 16);

        // Calculate marker position (clamp to ±100 cents for display)
        const clampedCents = Math.max(-100, Math.min(100, cents));
        const markerX = centerX + (clampedCents / 100) * (config.WIDTH / 2);

        // Determine color based on deviation
        let color;
        const absCents = Math.abs(cents);
        if (absCents <= tolerance) {
            color = config.COLORS.onTarget;
        } else if (cents > 0) {
            color = config.COLORS.sharp;
        } else {
            color = config.COLORS.flat;
        }

        // Draw marker
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.arc(markerX, y, 8, 0, Math.PI * 2);
        this.ctx.fill();

        // Draw inner marker
        this.ctx.fillStyle = config.COLORS.marker;
        this.ctx.beginPath();
        this.ctx.arc(markerX, y, 3, 0, Math.PI * 2);
        this.ctx.fill();

        // Draw cents value
        this.ctx.fillStyle = color;
        this.ctx.font = 'bold 14px Arial';
        this.ctx.textAlign = 'center';
        const centsText = cents > 0 ? `+${Math.round(cents)}` : `${Math.round(cents)}`;
        this.ctx.fillText(`${centsText}¢`, centerX, y + 22);

        // Draw arrow and label
        let symbol, label;
        if (absCents <= tolerance) {
            symbol = '✓';
            label = 'On Target';
        } else if (cents > 0) {
            symbol = '▲';
            label = 'Sharp';
        } else {
            symbol = '▼';
            label = 'Flat';
        }

        // Draw symbol
        this.ctx.font = 'bold 18px Arial';
        this.ctx.fillText(symbol, centerX - config.WIDTH / 2 - 30, y + 5);

        // Draw label on the right
        this.ctx.font = '12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.fillText(label, centerX + config.WIDTH / 2 + 15, y + 4);
    }

    /**
     * Helper to draw rounded rectangles
     */
    roundRect(x, y, width, height, radius) {
        this.ctx.moveTo(x + radius, y);
        this.ctx.lineTo(x + width - radius, y);
        this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        this.ctx.lineTo(x + width, y + height - radius);
        this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        this.ctx.lineTo(x + radius, y + height);
        this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        this.ctx.lineTo(x, y + radius);
        this.ctx.quadraticCurveTo(x, y, x + radius, y);
        this.ctx.closePath();
    }
}


/* === js/game/SongPlayer.js === */
/**
 * SongPlayer - Manages song timing and note spawning
 */
class SongPlayer {
    constructor(song) {
        this.song = song;
        this.beatsPerSecond = song.tempo / 60;
        this.currentTime = 0;
        this.noteIndex = 0;
        this.isComplete = false;
        this.spawnTimeOffset = 0;

        // Calculate lead time based on scroll distance and speed
        // Notes need time to scroll from spawn position to target
        this.leadTime = (GAME_CONFIG.SPAWN_X - GAME_CONFIG.TARGET_X) / GAME_CONFIG.SCROLL_SPEED;

        // Cache total duration (before prepareNotes modifies it)
        this.totalDuration = this.calculateTotalDuration();

        // Pre-calculate spawn times for each note (may set spawnTimeOffset)
        this.scheduledNotes = this.prepareNotes();
    }

    /**
     * Calculate total duration of the song in seconds
     */
    calculateTotalDuration() {
        let totalBeats = 0;
        for (const note of this.song.notes) {
            totalBeats += note.duration;
        }
        return totalBeats / this.beatsPerSecond;
    }

    /**
     * Prepare all notes with their spawn times
     */
    prepareNotes() {
        let beatPosition = 0;
        const notes = [];
        const leadTime = (GAME_CONFIG.SPAWN_X - GAME_CONFIG.TARGET_X) / GAME_CONFIG.SCROLL_SPEED;
        const totalNotes = this.song.notes.length;
        let lastWasBonus = false;

        for (let i = 0; i < this.song.notes.length; i++) {
            const note = this.song.notes[i];

            // Calculate when this note should be at the target (in seconds)
            const targetTime = beatPosition / this.beatsPerSecond;

            // Spawn early so note has time to scroll to target
            const spawnTime = targetTime - leadTime;

            // Bonus note chance: 12%, but never first/last note or consecutive
            const canBeBonus = i > 0 && i < totalNotes - 1 && !lastWasBonus;
            const isBonus = canBeBonus && Math.random() < 0.12;
            lastWasBonus = isBonus;

            notes.push({
                ...note,
                beatPosition: beatPosition,
                targetTime: targetTime,
                spawnTime: spawnTime,
                x: GAME_CONFIG.SPAWN_X,
                status: 'pending',
                scored: false,
                inScoringZone: false,
                isBonus: isBonus
            });

            beatPosition += note.duration;
        }

        // Fix: Offset all spawn times so earliest note spawns at time 0
        // This prevents early notes from all spawning immediately due to negative spawn times
        if (notes.length > 0) {
            const minSpawnTime = Math.min(...notes.map(n => n.spawnTime));
            if (minSpawnTime < 0) {
                const offset = -minSpawnTime;
                for (const note of notes) {
                    note.spawnTime += offset;
                    note.targetTime += offset;
                }
                this.spawnTimeOffset = offset;
            } else {
                this.spawnTimeOffset = 0;
            }
        }

        return notes;
    }

    /**
     * Update timing and return any notes that should spawn
     */
    update(deltaTime) {
        this.currentTime += deltaTime;
        const notesToSpawn = [];

        // Check if any notes should spawn now
        while (this.noteIndex < this.scheduledNotes.length) {
            const note = this.scheduledNotes[this.noteIndex];

            // Spawn notes at their calculated spawn time (before target time)
            if (this.currentTime >= note.spawnTime) {
                notesToSpawn.push({
                    ...note,
                    x: GAME_CONFIG.SPAWN_X
                });
                this.noteIndex++;
            } else {
                break;
            }
        }

        // Check if song is complete (all notes spawned and enough time passed)
        if (this.noteIndex >= this.scheduledNotes.length &&
            this.currentTime > this.totalDuration + 2) {
            this.isComplete = true;
        }

        return notesToSpawn;
    }

    /**
     * Get total duration of the song in seconds (including spawn time offset)
     */
    getTotalDuration() {
        return this.totalDuration + (this.spawnTimeOffset || 0);
    }

    /**
     * Get the total number of notes in the song
     */
    getNoteCount() {
        return this.song.notes.length;
    }

    /**
     * Check if the song has finished
     */
    isSongComplete() {
        return this.isComplete;
    }

    /**
     * Reset the song player
     */
    reset() {
        this.currentTime = 0;
        this.noteIndex = 0;
        this.isComplete = false;
    }
}


/* === js/game/ParticleSystem.js === */
/**
 * ParticleSystem - Visual effects for game feedback
 */
class ParticleSystem {
    constructor(ctx) {
        this.ctx = ctx;
        this.particles = [];
        this.textParticles = []; // Separate array for text effects
    }

    /**
     * Emit particles for a successful hit
     */
    emitHit(x, y) {
        const colors = ['#4CAF50', '#8BC34A', '#CDDC39', '#FFD700'];

        // Main particles (increased from 15 to 20)
        for (let i = 0; i < 20; i++) {
            this.particles.push({
                x: x,
                y: y,
                vx: (Math.random() - 0.5) * 10,
                vy: (Math.random() - 0.5) * 10 - 3,
                size: Math.random() * 6 + 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: Math.random() * 0.02 + 0.015
            });
        }

        // Fast "spark" particles
        for (let i = 0; i < 8; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 8 + 6;
            this.particles.push({
                x: x,
                y: y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: Math.random() * 2 + 1,
                color: '#fff',
                life: 1,
                decay: 0.05 // Fast decay for sparks
            });
        }
    }

    /**
     * Emit particles for a miss
     */
    emitMiss(x, y) {
        const colors = ['#E74C3C', '#C0392B', '#ff6b6b'];
        for (let i = 0; i < 8; i++) {
            this.particles.push({
                x: x,
                y: y,
                vx: (Math.random() - 0.5) * 5,
                vy: Math.random() * 3 + 1,
                size: Math.random() * 4 + 2,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: Math.random() * 0.03 + 0.02
            });
        }
    }

    /**
     * Emit burst for streak milestones
     */
    emitStreakBurst(x, y) {
        const colors = ['#FFD700', '#FFA500', '#FF6B6B', '#fff'];
        for (let i = 0; i < 25; i++) {
            const angle = (Math.PI * 2 / 25) * i;
            const speed = Math.random() * 5 + 3;
            this.particles.push({
                x: x,
                y: y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: Math.random() * 5 + 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: Math.random() * 0.015 + 0.01
            });
        }
    }

    /**
     * Update particle physics
     */
    update(deltaTime) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.15; // gravity
            p.life -= p.decay;

            if (p.life <= 0) {
                this.particles.splice(i, 1);
            }
        }
    }

    /**
     * Draw all particles
     */
    draw() {
        for (const p of this.particles) {
            this.ctx.save();
            this.ctx.globalAlpha = p.life;
            this.ctx.fillStyle = p.color;
            this.ctx.shadowColor = p.color;
            this.ctx.shadowBlur = 5;
            this.ctx.beginPath();
            this.ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.restore();
        }
    }

    /**
     * Emit confetti for celebrations (used on results screen)
     * @param {number} intensity - 1 for full (S-grade), 0.5 for medium, 0.25 for small
     */
    emitConfetti(intensity = 1) {
        const colors = ['#FFD700', '#FF6B6B', '#4CAF50', '#4A90D9', '#FF9F43', '#fff'];
        const count = Math.floor(50 * intensity);
        const canvasWidth = this.ctx.canvas.width;

        for (let i = 0; i < count; i++) {
            // Start from top, spread across width
            this.particles.push({
                x: Math.random() * canvasWidth,
                y: -10,
                vx: (Math.random() - 0.5) * 4,
                vy: Math.random() * 3 + 2,
                size: Math.random() * 6 + 4,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: 0.005 + Math.random() * 0.005, // Slow decay for confetti
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.2,
                isConfetti: true
            });
        }
    }

    /**
     * Emit near-miss amber particles
     */
    emitNearMiss(x, y) {
        const colors = ['#FFA500', '#FFD700', '#FF9F43'];
        for (let i = 0; i < 6; i++) {
            this.particles.push({
                x: x,
                y: y,
                vx: (Math.random() - 0.5) * 4,
                vy: (Math.random() - 0.5) * 4,
                size: Math.random() * 4 + 2,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: Math.random() * 0.03 + 0.02
            });
        }
    }

    /**
     * Emit floating score text
     * @param {string} text - Text to display (e.g., "+100")
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {string} color - Text color
     */
    emitScoreText(text, x, y, color = '#FFD700') {
        this.textParticles.push({
            text: text,
            x: x,
            y: y,
            vy: -2, // Float upward
            life: 1,
            decay: 0.02,
            color: color,
            scale: 1
        });
    }

    /**
     * Update particle physics
     */
    update(deltaTime) {
        // Update regular particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.x += p.vx;
            p.y += p.vy;

            // Different gravity for confetti (flutter down slowly)
            if (p.isConfetti) {
                p.vy += 0.05;
                p.vx += (Math.random() - 0.5) * 0.3; // Drift sideways
                if (p.rotation !== undefined) {
                    p.rotation += p.rotationSpeed;
                }
            } else {
                p.vy += 0.15; // Normal gravity
            }

            p.life -= p.decay;

            if (p.life <= 0) {
                this.particles.splice(i, 1);
            }
        }

        // Update text particles
        for (let i = this.textParticles.length - 1; i >= 0; i--) {
            const t = this.textParticles[i];
            t.y += t.vy;
            t.life -= t.decay;
            t.scale = 0.8 + t.life * 0.4; // Scale down as it fades

            if (t.life <= 0) {
                this.textParticles.splice(i, 1);
            }
        }
    }

    /**
     * Draw all particles
     */
    draw() {
        // Draw regular particles
        for (const p of this.particles) {
            this.ctx.save();
            this.ctx.globalAlpha = p.life;
            this.ctx.fillStyle = p.color;
            this.ctx.shadowColor = p.color;
            this.ctx.shadowBlur = 5;

            if (p.isConfetti && p.rotation !== undefined) {
                // Draw confetti as rotating rectangles
                this.ctx.translate(p.x, p.y);
                this.ctx.rotate(p.rotation);
                this.ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
            } else {
                // Draw regular particles as circles
                this.ctx.beginPath();
                this.ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
                this.ctx.fill();
            }

            this.ctx.restore();
        }

        // Draw text particles
        for (const t of this.textParticles) {
            this.ctx.save();
            this.ctx.globalAlpha = t.life;
            this.ctx.fillStyle = t.color;
            this.ctx.font = `bold ${Math.floor(18 * t.scale)}px Arial`;
            this.ctx.textAlign = 'center';
            this.ctx.shadowColor = t.color;
            this.ctx.shadowBlur = 8;
            this.ctx.fillText(t.text, t.x, t.y);
            this.ctx.restore();
        }
    }

    /**
     * Clear all particles
     */
    clear() {
        this.particles = [];
        this.textParticles = [];
    }
}


/* === js/game/GameEngine.js === */
/**
 * GameEngine - Main game loop and state management
 */
class GameEngine {
    constructor(canvas, audioManager, pitchDetector, toneGenerator = null, soundManager = null) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.audioManager = audioManager;
        this.pitchDetector = pitchDetector;
        this.toneGenerator = toneGenerator;
        this.soundManager = soundManager;

        this.renderer = new NoteRenderer(this.ctx);
        this.scoreManager = new ScoreManager();
        this.particleSystem = new ParticleSystem(this.ctx);
        this.songPlayer = null;

        // Screen flash effect state
        this.screenFlash = { type: null, intensity: 0 };
        this.lastStreak = 0; // Track streak milestones

        this.state = 'idle'; // 'idle', 'countdown', 'playing', 'finished'
        this.lastTimestamp = 0;
        this.activeNotes = [];
        this.currentPitch = null;
        this.feedback = null;

        this.countdownValue = 3;
        this.countdownStartTime = 0;

        this.onGameEnd = null; // Callback when game ends

        // Practice mode (blocking) - notes wait until hit correctly
        this.practiceMode = false;
        this.blockedNote = null; // Reference to note currently blocking progress
        this.blockedNoteTime = 0; // Time the note has been blocked
        this.maxBlockedTime = 10; // Max seconds before auto-skip (prevents softlock)

        // Melody mode - play notes as they scroll (super easy mode)
        this.melodyMode = false;

        // Animation frame tracking for proper cleanup
        this.animationFrameId = null;
    }

    /**
     * Set practice mode (blocking mode)
     */
    setPracticeMode(enabled) {
        this.practiceMode = enabled;
    }

    /**
     * Set melody mode (plays notes as they scroll - super easy mode)
     */
    setMelodyMode(enabled) {
        this.melodyMode = enabled;
    }

    /**
     * Start playing a song
     */
    startSong(song) {
        this.songPlayer = new SongPlayer(song);
        this.activeNotes = [];
        this.scoreManager.reset();
        this.scoreManager.setTotalNotes(song.notes.length);
        this.feedback = null;
        this.blockedNote = null;
        this.blockedNoteTime = 0;
        this.particleSystem.clear();
        this.screenFlash = { type: null, intensity: 0 };
        this.lastStreak = 0;

        // Reset pitch detector history for clean start
        if (this.pitchDetector.reset) {
            this.pitchDetector.reset();
        }

        // Start countdown
        this.state = 'countdown';
        this.countdownValue = 3;
        this.countdownStartTime = performance.now();

        this.lastTimestamp = performance.now();
        this.gameLoop(this.lastTimestamp);
    }

    /**
     * Main game loop
     */
    gameLoop(timestamp) {
        // Clamp deltaTime to prevent huge jumps when tab is backgrounded
        const deltaTime = Math.min((timestamp - this.lastTimestamp) / 1000, 0.1);
        this.lastTimestamp = timestamp;

        // Update based on state
        if (this.state === 'countdown') {
            this.updateCountdown(timestamp);
        } else if (this.state === 'playing') {
            this.update(deltaTime);
        }

        // Render
        this.render();

        // Continue loop if not finished
        if (this.state !== 'finished' && this.state !== 'idle') {
            this.animationFrameId = requestAnimationFrame((ts) => this.gameLoop(ts));
        } else {
            this.animationFrameId = null;
        }
    }

    /**
     * Update countdown state
     */
    updateCountdown(timestamp) {
        const elapsed = (timestamp - this.countdownStartTime) / 1000;
        this.countdownValue = 3 - Math.floor(elapsed);

        // Start playing when countdown reaches 0 (after showing 1)
        if (this.countdownValue < 1) {
            this.state = 'playing';
            this.lastTimestamp = timestamp;
        }
    }

    /**
     * Update game state
     */
    update(deltaTime) {
        // Detect current pitch
        const pitchResult = this.pitchDetector.detectPitch();
        this.currentPitch = pitchResult;

        // Spawn new notes (always, timing is handled by songPlayer)
        const newNotes = this.songPlayer.update(deltaTime);
        this.activeNotes.push(...newNotes);

        // In practice mode: find the first unscored note that should block
        if (this.practiceMode && !this.blockedNote) {
            for (const note of this.activeNotes) {
                if (!note.scored && note.x <= GAME_CONFIG.TARGET_X) {
                    this.blockedNote = note;
                    this.blockedNoteTime = 0;
                    note.inScoringZone = true;
                    note.status = 'active';
                    break;
                }
            }
        }

        // Track blocked note time and auto-skip if stuck too long
        if (this.practiceMode && this.blockedNote) {
            this.blockedNoteTime += deltaTime;

            // Auto-skip after timeout to prevent softlock
            if (this.blockedNoteTime >= this.maxBlockedTime) {
                const noteY = this.renderer.midiToY(this.blockedNote.midi);
                this.particleSystem.emitMiss(this.blockedNote.x, noteY);
                this.screenFlash = { type: 'miss', intensity: 0.7 };

                this.blockedNote.status = 'missed';
                this.blockedNote.scored = true;
                this.scoreManager.addMiss();
                this.showFeedback(false);

                // Play miss sound
                if (this.soundManager) {
                    this.soundManager.playMiss();
                }

                this.blockedNote = null;
                this.blockedNoteTime = 0;
            }
        }

        // Update note positions
        for (let i = 0; i < this.activeNotes.length; i++) {
            const note = this.activeNotes[i];

            if (this.practiceMode) {
                if (note === this.blockedNote) {
                    // Blocked note stays at target
                    note.x = GAME_CONFIG.TARGET_X;
                } else if (note.scored) {
                    // Scored notes scroll away
                    note.x -= GAME_CONFIG.SCROLL_SPEED * deltaTime;
                } else if (this.blockedNote) {
                    // Unscored notes behind blocked note: queue up
                    const blockedIdx = this.activeNotes.indexOf(this.blockedNote);
                    if (i > blockedIdx) {
                        const minDistance = GAME_CONFIG.NOTE_WIDTH * 1.2;
                        const queuePosition = i - blockedIdx;
                        const targetX = GAME_CONFIG.TARGET_X + minDistance * queuePosition;
                        if (note.x <= targetX) {
                            note.x = targetX;
                        } else {
                            note.x -= GAME_CONFIG.SCROLL_SPEED * deltaTime;
                        }
                    } else {
                        // Notes before blocked note (shouldn't happen, but scroll normally)
                        note.x -= GAME_CONFIG.SCROLL_SPEED * deltaTime;
                    }
                } else {
                    // No blocked note yet, scroll normally
                    note.x -= GAME_CONFIG.SCROLL_SPEED * deltaTime;
                }
            } else {
                // Normal mode: continuous scrolling
                note.x -= GAME_CONFIG.SCROLL_SPEED * deltaTime;
            }

            // Check if note is entering/exiting scoring zone
            if (!this.practiceMode) {
                const distanceToTarget = Math.abs(note.x - GAME_CONFIG.TARGET_X);
                const inZone = distanceToTarget < GAME_CONFIG.SCORING_ZONE_WIDTH / 2;

                if (inZone && !note.inScoringZone) {
                    note.inScoringZone = true;
                    // Only set active if not in melody mode (melody mode handles highlighting separately)
                    if (!this.melodyMode) {
                        note.status = 'active';
                    }
                } else if (!inZone && note.inScoringZone && !note.scored) {
                    // Note exited scoring zone without being hit - no longer scorable
                    note.inScoringZone = false;
                    // Reset status back to pending when exiting zone
                    if (note.status === 'active') {
                        note.status = 'pending';
                    }
                }
            }

            // Check scoring
            const canScore = this.practiceMode
                ? (note === this.blockedNote)
                : note.inScoringZone;

            if (canScore && !note.scored) {
                this.checkNoteHit(note);
            }

            // Mark as missed if passed without scoring (normal mode only)
            if (!this.practiceMode) {
                if (note.x < GAME_CONFIG.TARGET_X - GAME_CONFIG.SCORING_ZONE_WIDTH / 2 && !note.scored) {
                    note.scored = true;
                    const noteY = this.renderer.midiToY(note.midi);

                    // Try to use shield
                    const shieldUsed = this.scoreManager.addMiss();

                    if (shieldUsed) {
                        // Shield consumed - streak preserved!
                        note.status = 'shielded';
                        this.particleSystem.emitScoreText('SAVED!', note.x, noteY - 20, '#4A90D9');
                        this.screenFlash = { type: 'streak', intensity: 0.8 };

                        if (this.soundManager) {
                            this.soundManager.playShieldUsed();
                        }
                    } else {
                        // No shield - normal miss
                        note.status = 'missed';
                        this.showFeedback(false);
                        this.particleSystem.emitMiss(note.x, noteY);
                        this.screenFlash = { type: 'miss', intensity: 0.7 };

                        if (this.soundManager) {
                            this.soundManager.playMiss();
                        }
                    }
                }
            }
        }

        // Remove notes that have scrolled off screen
        this.activeNotes = this.activeNotes.filter(n => n.x > -GAME_CONFIG.NOTE_WIDTH * 2);

        // Clear blocked note reference if it was removed
        if (this.blockedNote && !this.activeNotes.includes(this.blockedNote)) {
            this.blockedNote = null;
        }

        // Update feedback animation
        if (this.feedback) {
            this.feedback.opacity -= deltaTime * 1.5;
            this.feedback.y -= deltaTime * 40;
            if (this.feedback.opacity <= 0) {
                this.feedback = null;
            }
        }

        // Update particle system
        this.particleSystem.update(deltaTime);

        // Update screen flash
        if (this.screenFlash.intensity > 0) {
            this.screenFlash.intensity -= deltaTime * 4;
            if (this.screenFlash.intensity <= 0) {
                this.screenFlash = { type: null, intensity: 0 };
            }
        }

        // Check for streak milestones
        const currentStreak = this.scoreManager.getStreak();
        if (currentStreak >= 5 && currentStreak % 5 === 0 && currentStreak > this.lastStreak) {
            this.screenFlash = { type: 'streak', intensity: 1 };
            this.particleSystem.emitStreakBurst(this.canvas.width / 2, this.canvas.height / 2);

            // Play streak milestone sound
            if (this.soundManager) {
                this.soundManager.playStreakMilestone();
            }

            // Check if shield was just awarded
            if (this.scoreManager.wasShieldAwarded()) {
                this.particleSystem.emitScoreText('SHIELD!', this.canvas.width / 2, this.canvas.height / 2 - 40, '#4A90D9');
                if (this.soundManager) {
                    this.soundManager.playShieldActivate();
                }
            }
        }
        this.lastStreak = currentStreak;

        // Play reference tone or melody guide
        if (this.toneGenerator) {
            // Melody mode: play notes as they scroll across
            // Reference tone: play only when note is in scoring zone
            const noteToPlay = this.melodyMode
                ? this.getMelodyNote()
                : this.getActiveNote();

            if (noteToPlay) {
                this.toneGenerator.playMidi(noteToPlay.midi);

                // In melody mode, highlight the note currently being played
                if (this.melodyMode && noteToPlay.status === 'pending') {
                    noteToPlay.status = 'active';
                }
            } else {
                this.toneGenerator.stop();
            }

            // In melody mode, reset status for notes that are no longer being played
            if (this.melodyMode) {
                for (const note of this.activeNotes) {
                    if (note !== noteToPlay && note.status === 'active' && !note.scored) {
                        note.status = 'pending';
                    }
                }
            }
        }

        // Check if song is complete
        if (this.songPlayer.isSongComplete() && this.activeNotes.length === 0) {
            this.state = 'finished';
            if (this.toneGenerator) {
                this.toneGenerator.stop();
            }
            if (this.onGameEnd) {
                this.onGameEnd(this.scoreManager.getResults());
            }
        }
    }

    /**
     * Get the currently active note for pitch guide
     */
    getActiveNote() {
        if (this.practiceMode && this.blockedNote) {
            return this.blockedNote;
        }
        // Find first note in scoring zone
        for (const note of this.activeNotes) {
            if (note.inScoringZone && !note.scored) {
                return note;
            }
        }
        return null;
    }

    /**
     * Get the note to play for melody guide based on song timing
     * Uses the note's target time and duration to determine which note should be playing
     */
    getMelodyNote() {
        if (!this.songPlayer) return null;

        const currentTime = this.songPlayer.currentTime;

        // Find the note that should be playing at the current time
        // A note plays from its targetTime until targetTime + duration (in seconds)
        for (const note of this.activeNotes) {
            if (note.scored) continue;

            // Calculate note's time window
            const noteStartTime = note.targetTime;
            const noteDurationSeconds = note.duration / this.songPlayer.beatsPerSecond;
            const noteEndTime = noteStartTime + noteDurationSeconds;

            // Check if current time falls within this note's window
            if (currentTime >= noteStartTime && currentTime < noteEndTime) {
                return note;
            }
        }

        // Fallback: if no note is in its time window, find the next upcoming note
        // This handles the gap between notes
        let nextNote = null;
        let smallestDiff = Infinity;

        for (const note of this.activeNotes) {
            if (note.scored) continue;

            const diff = note.targetTime - currentTime;
            // Only consider upcoming notes (not past ones)
            if (diff >= 0 && diff < smallestDiff) {
                smallestDiff = diff;
                nextNote = note;
            }
        }

        // Only highlight upcoming note if it's very close (within 0.1 seconds)
        if (nextNote && smallestDiff < 0.1) {
            return nextNote;
        }

        return null;
    }

    /**
     * Check if the current pitch matches the note
     */
    checkNoteHit(note) {
        if (!this.currentPitch) {
            return; // No pitch detected, wait
        }

        const targetFreq = midiToFrequency(note.midi);
        const centsDeviation = Math.abs(getCentsDeviation(this.currentPitch.frequency, targetFreq));
        const isAccurate = centsDeviation <= GAME_CONFIG.PITCH_TOLERANCE_CENTS;

        // Near-miss detection (within 75-100 cents, outside normal tolerance)
        const isNearMiss = !isAccurate && centsDeviation <= 100 && centsDeviation > GAME_CONFIG.PITCH_TOLERANCE_CENTS;

        if (isNearMiss && !note.hadNearMiss) {
            // Show near-miss feedback (only once per note)
            note.hadNearMiss = true;
            const noteY = this.renderer.midiToY(note.midi);

            // Emit near-miss particles and feedback
            this.particleSystem.emitNearMiss(GAME_CONFIG.TARGET_X, noteY);
            this.particleSystem.emitScoreText('Almost!', GAME_CONFIG.TARGET_X, noteY - 20, '#FFA500');
            this.screenFlash = { type: 'nearMiss', intensity: 0.5 };

            // Play near-miss sound
            if (this.soundManager) {
                this.soundManager.playNearMiss();
            }
        }

        if (isAccurate) {
            note.status = 'hit';
            note.scored = true;

            // Bonus notes give 2x points
            const basePoints = note.isBonus ? GAME_CONFIG.PERFECT_SCORE * 2 : GAME_CONFIG.PERFECT_SCORE;
            const earnedPoints = this.scoreManager.addHit(basePoints);
            this.showFeedback(true);

            // Emit hit particles and screen flash
            const noteY = this.renderer.midiToY(note.midi);
            this.particleSystem.emitHit(GAME_CONFIG.TARGET_X, noteY);
            this.screenFlash = { type: 'hit', intensity: 1 };

            // Emit score flyup text
            const scoreColor = note.isBonus ? '#FFD700' : '#4CAF50';
            const scoreText = note.isBonus ? `+${earnedPoints} BONUS!` : `+${earnedPoints}`;
            this.particleSystem.emitScoreText(scoreText, GAME_CONFIG.TARGET_X, noteY - 20, scoreColor);

            // Play appropriate sound
            if (this.soundManager) {
                if (note.isBonus) {
                    this.soundManager.playBonus();
                } else {
                    this.soundManager.playHit();
                }
            }

            // In practice mode, clear the block so next note can advance
            if (this.practiceMode && note === this.blockedNote) {
                this.blockedNote = null;
                this.blockedNoteTime = 0;
            }
        }
    }

    /**
     * Show hit/miss feedback
     */
    showFeedback(hit) {
        this.feedback = {
            hit: hit,
            opacity: 1,
            y: 0
        };
    }

    /**
     * Render the game
     */
    render() {
        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw background and guide lines
        this.renderer.drawBackground();
        this.renderer.drawTargetLine();

        // Draw notes
        for (const note of this.activeNotes) {
            this.renderer.drawNote(note);
        }

        // Draw practice mode blocked note indicator
        if (this.practiceMode && this.blockedNote && this.state === 'playing') {
            const timeRemaining = this.maxBlockedTime - this.blockedNoteTime;
            const pitchFreq = this.currentPitch ? this.currentPitch.frequency : null;
            this.renderer.drawBlockedIndicator(
                this.blockedNote,
                timeRemaining,
                this.maxBlockedTime,
                pitchFreq
            );
        }

        // Draw particles
        this.particleSystem.draw();

        // Draw player's current pitch indicator
        if (this.currentPitch && this.state === 'playing') {
            this.renderer.drawPitchIndicator(this.currentPitch.frequency);

            // Draw pitch guide for active note
            const activeNote = this.getActiveNote();
            if (activeNote) {
                this.renderer.drawPitchGuide(this.currentPitch.frequency, activeNote.midi);
            }
        }

        // Draw feedback
        this.renderer.drawFeedback(this.feedback);

        // Draw screen flash effect
        this.renderer.drawScreenFlash(this.screenFlash.type, this.screenFlash.intensity);

        // Draw progress bar
        if (this.songPlayer && this.state === 'playing') {
            this.renderer.drawProgressBar(
                this.songPlayer.currentTime,
                this.songPlayer.getTotalDuration()
            );
        }

        // Draw score
        this.renderer.drawScore(
            this.scoreManager.getScore(),
            this.scoreManager.getStreak(),
            this.scoreManager.hasStreakShield()
        );

        // Draw countdown if in countdown state
        if (this.state === 'countdown' && this.countdownValue > 0) {
            this.renderer.drawCountdown(this.countdownValue);
        }
    }

    /**
     * Stop the game
     */
    stop() {
        this.state = 'idle';

        // Cancel pending animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Stop reference tone
        if (this.toneGenerator) {
            this.toneGenerator.stop();
        }
    }

    /**
     * Check if game is currently playing
     */
    isPlaying() {
        return this.state === 'playing' || this.state === 'countdown';
    }
}


/* === js/main.js === */
/**
 * Main entry point - Game initialization and UI management
 */

// Global instances
let audioManager = null;
let pitchDetector = null;
let toneGenerator = null;
let soundManager = null;
let gameEngine = null;

// DOM elements
let canvas;
let startButton;
let songSelect;
let statusText;
let menuDiv;
let gameContainer;
let resultsDiv;
let resultsContent;
let resultsGrade;
let resultsBanner;
let resultsEncouragement;
let playAgainButton;
let shareButton;
let practiceModeToggle;
let modeText;
let modeDescription;
let referenceToneToggle;
let toneText;
let melodyGuideToggle;
let melodyText;
let soundEffectsToggle;
let soundText;
let importButton;
let fileInput;
let importMessage;
let formatHelpToggle;
let formatHelp;

// Custom songs loaded from localStorage
let customSongs = [];

// Current song being played (for personal best tracking)
let currentSong = null;

/**
 * Initialize the application
 */
function init() {
    // Get DOM elements
    canvas = document.getElementById('gameCanvas');
    startButton = document.getElementById('startButton');
    songSelect = document.getElementById('songSelect');
    statusText = document.getElementById('status');
    menuDiv = document.getElementById('menu');
    gameContainer = document.getElementById('gameContainer');
    resultsDiv = document.getElementById('results');
    resultsContent = document.getElementById('resultsContent');
    resultsGrade = document.getElementById('resultsGrade');
    resultsBanner = document.getElementById('resultsBanner');
    resultsEncouragement = document.getElementById('resultsEncouragement');
    playAgainButton = document.getElementById('playAgainButton');
    shareButton = document.getElementById('shareButton');
    practiceModeToggle = document.getElementById('practiceMode');
    modeText = document.getElementById('modeText');
    modeDescription = document.getElementById('modeDescription');
    referenceToneToggle = document.getElementById('referenceTone');
    toneText = document.getElementById('toneText');
    melodyGuideToggle = document.getElementById('melodyGuide');
    melodyText = document.getElementById('melodyText');
    soundEffectsToggle = document.getElementById('soundEffects');
    soundText = document.getElementById('soundText');
    importButton = document.getElementById('importButton');
    fileInput = document.getElementById('fileInput');
    importMessage = document.getElementById('importMessage');
    formatHelpToggle = document.getElementById('formatHelpToggle');
    formatHelp = document.getElementById('formatHelp');

    // Validate required elements exist
    if (!canvas || !startButton || !songSelect) {
        console.error('Required DOM elements not found');
        return;
    }

    // Set canvas size
    canvas.width = GAME_CONFIG.CANVAS_WIDTH;
    canvas.height = GAME_CONFIG.CANVAS_HEIGHT;

    // Load custom songs from localStorage
    loadCustomSongs();

    // Populate song select dropdown
    populateSongList();

    // Set up event listeners
    startButton.addEventListener('click', startGame);
    playAgainButton.addEventListener('click', returnToMenu);
    if (shareButton) {
        shareButton.addEventListener('click', shareScore);
    }
    practiceModeToggle.addEventListener('change', updateModeDisplay);
    referenceToneToggle.addEventListener('change', updateToneDisplay);
    melodyGuideToggle.addEventListener('change', updateMelodyDisplay);
    if (soundEffectsToggle) {
        soundEffectsToggle.addEventListener('change', updateSoundDisplay);
    }

    // Import song event listeners
    if (importButton && fileInput) {
        importButton.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', handleFileImport);
    }

    // Format help toggle
    if (formatHelpToggle && formatHelp) {
        formatHelpToggle.addEventListener('click', toggleFormatHelp);
    }

    // Draw initial canvas state
    drawIdleScreen();
}

/**
 * Load custom songs from localStorage
 */
function loadCustomSongs() {
    customSongs = SongImporter.loadFromStorage();
}

/**
 * Handle file import
 */
function handleFileImport(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input so same file can be selected again
    fileInput.value = '';

    // Read the file
    const reader = new FileReader();

    reader.onload = function(e) {
        try {
            const json = JSON.parse(e.target.result);
            const song = SongImporter.import(json);

            // Add to storage and update local array
            customSongs = SongImporter.addToStorage(song);

            // Refresh the song list
            populateSongList();

            // Select the newly imported song
            songSelect.value = song.id;

            // Show success message
            showImportMessage(`Imported "${song.name}" successfully!`, 'success');

        } catch (error) {
            showImportMessage(error.message, 'error');
        }
    };

    reader.onerror = function() {
        showImportMessage('Failed to read file', 'error');
    };

    reader.readAsText(file);
}

/**
 * Show import message (success or error)
 */
function showImportMessage(message, type) {
    if (!importMessage) return;

    importMessage.textContent = message;
    importMessage.className = 'import-message ' + type;
    importMessage.classList.remove('hidden');

    // Auto-hide after 5 seconds
    setTimeout(() => {
        importMessage.classList.add('hidden');
    }, 5000);
}

/**
 * Toggle the format help panel visibility
 */
function toggleFormatHelp() {
    const isExpanded = formatHelpToggle.getAttribute('aria-expanded') === 'true';
    formatHelpToggle.setAttribute('aria-expanded', !isExpanded);
    formatHelp.classList.toggle('hidden');
}

/**
 * Update the mode display text based on toggle state
 */
function updateModeDisplay() {
    if (practiceModeToggle.checked) {
        modeText.textContent = 'Practice';
        modeDescription.textContent = 'Notes wait until you hit them correctly';
    } else {
        modeText.textContent = 'Challenge';
        modeDescription.textContent = 'Notes scroll past - hit them in time!';
    }
}

/**
 * Update the tone display text based on toggle state
 */
function updateToneDisplay() {
    if (referenceToneToggle.checked) {
        toneText.textContent = 'On';
    } else {
        toneText.textContent = 'Off';
    }
    // Update tone generator if it exists
    if (toneGenerator) {
        toneGenerator.setEnabled(referenceToneToggle.checked || melodyGuideToggle.checked);
    }
    // Update game engine if it exists
    if (gameEngine) {
        gameEngine.setMelodyMode(melodyGuideToggle.checked);
    }
}

/**
 * Update the melody guide display text based on toggle state
 */
function updateMelodyDisplay() {
    if (melodyGuideToggle.checked) {
        melodyText.textContent = 'On';
    } else {
        melodyText.textContent = 'Off';
    }
    // Update tone generator - enable if either toggle is on
    if (toneGenerator) {
        toneGenerator.setEnabled(referenceToneToggle.checked || melodyGuideToggle.checked);
    }
    // Update game engine if it exists
    if (gameEngine) {
        gameEngine.setMelodyMode(melodyGuideToggle.checked);
    }
}

/**
 * Update the sound effects display text based on toggle state
 */
function updateSoundDisplay() {
    if (soundEffectsToggle.checked) {
        soundText.textContent = 'On';
    } else {
        soundText.textContent = 'Off';
    }
    // Update sound manager if it exists
    if (soundManager) {
        soundManager.setEnabled(soundEffectsToggle.checked);
    }
    // Save preference
    const settings = StorageManager.getSettings();
    settings.soundEffects = soundEffectsToggle.checked;
    StorageManager.saveSettings(settings);
}

/**
 * Get display text for a song with personal best info
 */
function getSongDisplayText(song) {
    const best = StorageManager.getSongBest(song.id);
    // Only show score if there's a valid score > 0 (not just a partial entry or 0 from quit)
    if (best && best.score > 0 && best.grade) {
        return `${song.name} [${best.grade}] ${best.score.toLocaleString()}`;
    }
    return song.name;
}

/**
 * Populate the song selection dropdown
 */
function populateSongList() {
    // Clear existing options
    songSelect.innerHTML = '';

    // Add built-in songs
    if (SONGS.length > 0) {
        const builtInGroup = document.createElement('optgroup');
        builtInGroup.label = 'Built-in Songs';
        SONGS.forEach(song => {
            const option = document.createElement('option');
            option.value = song.id;
            option.textContent = getSongDisplayText(song);
            builtInGroup.appendChild(option);
        });
        songSelect.appendChild(builtInGroup);
    }

    // Add custom songs
    if (customSongs.length > 0) {
        const customGroup = document.createElement('optgroup');
        customGroup.label = 'My Songs';
        customSongs.forEach(song => {
            const option = document.createElement('option');
            option.value = song.id;
            option.textContent = getSongDisplayText(song);
            customGroup.appendChild(option);
        });
        songSelect.appendChild(customGroup);
    }
}

/**
 * Draw the idle screen on canvas
 */
function drawIdleScreen() {
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = GAME_CONFIG.COLORS.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#444';
    ctx.font = '24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Select a song and click Start!', canvas.width / 2, canvas.height / 2);
}

/**
 * Start the game with the selected song
 */
async function startGame() {
    try {
        statusText.textContent = 'Requesting microphone access...';
        startButton.disabled = true;

        // Initialize audio (must be triggered by user interaction)
        if (!audioManager) {
            audioManager = new AudioManager();
            await audioManager.initialize();
            pitchDetector = new PitchDetector(audioManager);
            toneGenerator = new ToneGenerator(audioManager.getAudioContext());
            soundManager = new SoundManager(audioManager.getAudioContext());

            // Load saved sound settings
            const settings = StorageManager.getSettings();
            soundManager.setEnabled(settings.soundEffects);
            if (soundEffectsToggle) {
                soundEffectsToggle.checked = settings.soundEffects;
                soundText.textContent = settings.soundEffects ? 'On' : 'Off';
            }
        } else {
            await audioManager.resume();
        }

        // Configure tone generator based on toggles
        toneGenerator.setEnabled(referenceToneToggle.checked || melodyGuideToggle.checked);

        // Create game engine
        gameEngine = new GameEngine(canvas, audioManager, pitchDetector, toneGenerator, soundManager);

        // Set practice mode based on toggle
        gameEngine.setPracticeMode(practiceModeToggle.checked);

        // Set melody mode based on toggle
        gameEngine.setMelodyMode(melodyGuideToggle.checked);

        // Set up game end callback
        gameEngine.onGameEnd = showResults;

        // Get selected song (check built-in first, then custom)
        const selectedSongId = songSelect.value;
        let song = SONGS.find(s => s.id === selectedSongId);

        if (!song) {
            song = customSongs.find(s => s.id === selectedSongId);
        }

        if (!song) {
            throw new Error('Song not found');
        }

        // Store current song for personal best tracking
        currentSong = song;

        // Update UI
        statusText.textContent = 'Playing: ' + song.name;
        menuDiv.classList.add('hidden');
        gameContainer.classList.remove('hidden');
        resultsDiv.classList.add('hidden');

        // Start the game
        gameEngine.startSong(song);

    } catch (error) {
        console.error('Failed to start game:', error);

        // Reset globals to prevent inconsistent state
        if (audioManager) {
            audioManager.dispose();
        }
        audioManager = null;
        pitchDetector = null;
        toneGenerator = null;
        gameEngine = null;

        statusText.textContent = 'Error: ' + error.message;
        startButton.disabled = false;

        if (error.name === 'NotAllowedError') {
            statusText.textContent = 'Microphone access denied. Please allow microphone access and try again.';
        }
    }
}

/**
 * Create a result item element safely (no innerHTML)
 */
function createResultItem(label, value, className = '') {
    const item = document.createElement('div');
    item.className = 'result-item';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'result-label';
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.className = 'result-value' + (className ? ' ' + className : '');
    valueSpan.textContent = value;

    item.appendChild(labelSpan);
    item.appendChild(valueSpan);
    return item;
}

/**
 * Trigger grade celebration with sound and visual effects
 */
function triggerGradeCelebration(grade) {
    // Create a temporary canvas for confetti on results screen
    let celebrationCanvas = document.getElementById('celebrationCanvas');
    if (!celebrationCanvas) {
        celebrationCanvas = document.createElement('canvas');
        celebrationCanvas.id = 'celebrationCanvas';
        celebrationCanvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:1000;';
        document.body.appendChild(celebrationCanvas);
    }

    celebrationCanvas.width = window.innerWidth;
    celebrationCanvas.height = window.innerHeight;
    const ctx = celebrationCanvas.getContext('2d');
    const particleSystem = new ParticleSystem(ctx);

    // Different celebrations based on grade
    switch (grade) {
        case 'S':
            // Full celebration: fanfare + lots of confetti
            if (soundManager) {
                soundManager.playFanfare();
            }
            particleSystem.emitConfetti(1);
            // Emit more waves of confetti
            setTimeout(() => particleSystem.emitConfetti(0.7), 300);
            setTimeout(() => particleSystem.emitConfetti(0.5), 600);
            break;
        case 'A':
            // Good celebration: chord + medium confetti
            if (soundManager) {
                soundManager.playStreakMilestone();
            }
            particleSystem.emitConfetti(0.6);
            break;
        case 'B':
            // Small celebration: hit sound + small confetti
            if (soundManager) {
                soundManager.playHit();
            }
            particleSystem.emitConfetti(0.3);
            break;
        default:
            // No celebration for C, D, F
            break;
    }

    // Animate the celebration particles
    let animationId;
    function animate() {
        ctx.clearRect(0, 0, celebrationCanvas.width, celebrationCanvas.height);
        particleSystem.update(1/60);
        particleSystem.draw();

        if (particleSystem.particles.length > 0) {
            animationId = requestAnimationFrame(animate);
        } else {
            // Clean up when done
            cancelAnimationFrame(animationId);
            celebrationCanvas.remove();
        }
    }

    if (grade === 'S' || grade === 'A' || grade === 'B') {
        animate();
    }
}

/**
 * Show the results screen
 */
function showResults(results) {
    gameContainer.classList.add('hidden');
    resultsDiv.classList.remove('hidden');

    // Clear previous results
    resultsContent.innerHTML = '';
    resultsGrade.innerHTML = '';
    resultsBanner.classList.add('hidden');
    resultsEncouragement.classList.add('hidden');

    // Safely sanitize grade for CSS class (only allow A-Z, S)
    const safeGrade = String(results.grade).replace(/[^A-Za-z]/g, '').toUpperCase();

    // Save score and check for personal best
    let pbInfo = { isNewBest: false, previousBest: null, delta: null };
    if (currentSong) {
        pbInfo = StorageManager.saveSongScore(
            currentSong.id,
            results.score,
            results.grade,
            results.maxStreak,
            results.accuracy
        );

        // Update global stats
        StorageManager.updateStats({
            notesHit: results.notesHit,
            notesMissed: results.notesMissed,
            maxStreak: results.maxStreak
        });

        // Check achievements
        const newAchievements = StorageManager.checkAchievements(results, currentSong.tempo || 100);
        if (newAchievements.length > 0) {
            // Queue achievement notifications (will be implemented later)
            console.log('New achievements:', newAchievements);
        }
    }

    // Show personal best banner if new record
    if (pbInfo.isNewBest) {
        resultsBanner.textContent = 'NEW HIGH SCORE!';
        resultsBanner.className = 'results-banner new-record';
        resultsBanner.classList.remove('hidden');
    } else if (pbInfo.previousBest !== null && pbInfo.delta !== null) {
        // Show delta from previous best
        const deltaText = pbInfo.delta >= 0 ? `+${pbInfo.delta}` : `${pbInfo.delta}`;
        if (pbInfo.delta < 0 && Math.abs(pbInfo.delta) <= 50) {
            resultsBanner.textContent = `${Math.abs(pbInfo.delta)} points from your best!`;
            resultsBanner.className = 'results-banner near-best';
            resultsBanner.classList.remove('hidden');
        }
    }

    // Display grade with animation class
    const gradeSpan = document.createElement('span');
    gradeSpan.className = 'grade-display grade-' + safeGrade;
    gradeSpan.textContent = results.grade;
    resultsGrade.appendChild(gradeSpan);

    // Trigger grade celebration based on grade
    triggerGradeCelebration(results.grade);

    // Create result items using DOM methods (no XSS risk)
    resultsContent.appendChild(createResultItem('Score', results.score.toLocaleString()));
    resultsContent.appendChild(createResultItem('Accuracy', results.accuracy + '%'));
    resultsContent.appendChild(createResultItem('Notes Hit', results.notesHit + ' / ' + results.totalNotes));
    resultsContent.appendChild(createResultItem('Best Streak', results.maxStreak));

    // Show personal best for comparison if not a new record
    if (!pbInfo.isNewBest && pbInfo.previousBest !== null) {
        resultsContent.appendChild(createResultItem('Your Best', pbInfo.previousBest.toLocaleString(), 'personal-best'));
    }

    // Show encouragement message if close to next grade
    const nextGradeInfo = StorageManager.getNextGradeInfo(results.accuracy);
    if (nextGradeInfo && parseFloat(nextGradeInfo.percentNeeded) <= 5) {
        resultsEncouragement.textContent = `So close! Just ${nextGradeInfo.percentNeeded}% more for ${nextGradeInfo.nextGrade} grade!`;
        resultsEncouragement.classList.remove('hidden');
    }

    // Store results for share functionality
    lastResults = results;
}

// Store last results for sharing
let lastResults = null;

/**
 * Share score to clipboard
 */
function shareScore() {
    if (!lastResults || !currentSong) return;

    const shareText = `I scored ${lastResults.score.toLocaleString()} (${lastResults.grade} grade) on "${currentSong.name}" in Perfect Note! Can you beat me?`;

    navigator.clipboard.writeText(shareText).then(() => {
        // Show feedback
        const originalText = shareButton.textContent;
        shareButton.textContent = 'Copied!';
        setTimeout(() => {
            shareButton.textContent = originalText;
        }, 2000);
    }).catch(() => {
        // Fallback for older browsers
        alert(shareText);
    });
}

/**
 * Return to the main menu
 */
function returnToMenu() {
    // Stop any running game
    if (gameEngine) {
        gameEngine.stop();
    }

    resultsDiv.classList.add('hidden');
    menuDiv.classList.remove('hidden');
    gameContainer.classList.add('hidden');
    startButton.disabled = false;
    statusText.textContent = 'Click Start to begin';

    // Refresh song list to show updated personal bests
    populateSongList();

    // Re-select the song that was just played
    if (currentSong) {
        songSelect.value = currentSong.id;
    }

    drawIdleScreen();
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', init);

